<template>
  <div class="driver_operations_report_wrapper">
    <!-- Breadcrumb if used as standalone page -->
    <Breadcrumb :items="breadcrumbItems" v-if="showBreadcrumb" />

    <!-- Card Container -->
    <div class="custom_card">
      <!-- Section Header -->
      <div
        class="card-header d-flex align-items-center justify-content-between flex-wrap gap-3"
      >
        <div class="d-flex align-items-center gap-3">
          <div class="header_icon_box">
            <i class="fal fa-chart-line text-white"></i>
          </div>
          <div>
            <h4 class="card-title mb-1" style="font-weight: 700">
              تقرير نظام أوقات عمل وعمليات المندوبين
            </h4>
            <p class="text-muted mb-0" style="font-size: 13px">
              متابعة دقيقة لأوقات العمل الثابتة، فترات الإتاحة، التواجد بالزون،
              التأخير، وحالة المحفظة ونسبة قبول الطلبات
            </p>
          </div>
        </div>
        <div class="d-flex align-items-center gap-2">
          <button
            class="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
            @click="fetchReportData"
            :disabled="loading"
            title="تحديث البيانات"
          >
            <i class="fal fa-sync-alt" :class="{ 'fa-spin': loading }"></i>
            <span>تحديث</span>
          </button>
          <button
            class="btn btn-outline-success btn-sm d-flex align-items-center gap-1"
            @click="exportToCSV"
            :disabled="!rows.length"
          >
            <i class="fal fa-file-excel"></i>
            <span>تصدير البيانات</span>
          </button>
        </div>
      </div>

      <!-- Start Summary Cards Grid -->
      <div class="row g-3 my-3" v-if="rows.length > 0 || loading">
        <!-- Card 1: Scheduled Drivers -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_purple">
            <div class="card_icon">
              <i class="fal fa-id-card"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">مندوبين بجدول عمل</span>
              <h3 class="stat_val">
                {{
                  summary.scheduled_drivers_count ||
                  summary.drivers_with_working_times ||
                  0
                }}
              </h3>
              <span class="stat_sub">مندوب مجدول</span>
            </div>
          </div>
        </div>

        <!-- Card 2: Delayed Drivers -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_red">
            <div class="card_icon">
              <i class="fal fa-clock"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">المندوبين المتأخرين</span>
              <h3 class="stat_val">{{ summary.delayed_drivers_count || 0 }}</h3>
              <span class="stat_sub">حالة تأخير مسجلة</span>
            </div>
          </div>
        </div>

        <!-- Card 3: Approved Work Hours -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_green">
            <div class="card_icon">
              <i class="fal fa-check-circle"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">إجمالي الساعات المعتمدة</span>
              <h3 class="stat_val">
                {{
                  formatMinutesToHours(
                    summary.total_approved_work_minutes ||
                      summary.approved_work_minutes
                  )
                }}
              </h3>
              <span class="stat_sub">{{
                formatMinutesDetail(
                  summary.total_approved_work_minutes ||
                    summary.approved_work_minutes
                )
              }}</span>
            </div>
          </div>
        </div>

        <!-- Card 4: Average Acceptance Rate -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_cyan">
            <div class="card_icon">
              <i class="fal fa-percentage"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">متوسط نسبة القبول</span>
              <h3 class="stat_val">
                {{
                  formatPercentage(
                    summary.average_acceptance_rate || summary.acceptance_rate
                  )
                }}%
              </h3>
              <span class="stat_sub">قبول الطلبات المعروضة</span>
            </div>
          </div>
        </div>

        <!-- Card 5: Wallet Lock Excluded -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_orange">
            <div class="card_icon">
              <i class="fal fa-wallet"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">مستبعد (إغلاق المحفظة)</span>
              <h3 class="stat_val">
                {{
                  formatMinutesToHours(
                    summary.total_wallet_locked_excluded_minutes ||
                      summary.wallet_locked_excluded_minutes
                  )
                }}
              </h3>
              <span class="stat_sub">{{
                formatMinutesDetail(
                  summary.total_wallet_locked_excluded_minutes ||
                    summary.wallet_locked_excluded_minutes
                )
              }}</span>
            </div>
          </div>
        </div>

        <!-- Card 6: Outside Zone Time -->
        <div class="col-xl-2 col-lg-4 col-md-6">
          <div class="ops_stat_card card_amber">
            <div class="card_icon">
              <i class="fal fa-map-marker-slash"></i>
            </div>
            <div class="card_info">
              <span class="stat_label">التواجد خارج الزون</span>
              <h3 class="stat_val">
                {{
                  formatMinutesToHours(
                    summary.total_outside_zone_minutes ||
                      summary.outside_zone_minutes
                  )
                }}
              </h3>
              <span class="stat_sub">{{
                formatMinutesDetail(
                  summary.total_outside_zone_minutes ||
                    summary.outside_zone_minutes
                )
              }}</span>
            </div>
          </div>
        </div>
      </div>
      <!-- End Summary Cards Grid -->

      <!-- Secondary Orders & Nominations Summary Banner -->
      <div
        class="ops_nominations_summary_banner p-3 mb-4 rounded-xl"
        v-if="rows.length > 0"
      >
        <div
          class="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-2"
        >
          <div class="d-flex align-items-center gap-2">
            <i
              class="fal fa-boxes-alt text-primary"
              style="font-size: 16px"
            ></i>
            <span
              class="font-weight-bold"
              style="font-size: 13px; color: #1e293b"
            >
              إجمالي ترشيحات وطلبات الفترة المحددة:
            </span>
          </div>
          <span class="text-muted" style="font-size: 11.5px">
            إجمالي التقرير لجميع المندوبين
          </span>
        </div>
        <div class="d-flex align-items-center flex-wrap gap-2">
          <div
            class="summary_mini_pill pill_purple"
            title="الطلبات المسندة إداريًا"
          >
            <i class="fal fa-user-tag"></i>
            <span
              >الطلبات المسندة إداريًا:
              <strong>{{
                summary.total_assigned_orders_count || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_red"
            title="الترشيحات المنتهية المهلة"
          >
            <i class="fal fa-hourglass-end"></i>
            <span
              >الترشيحات المنتهية المهلة:
              <strong>{{
                summary.total_expired_orders_count || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_amber"
            title="الترشيحات التي أخذها سائق آخر"
          >
            <i class="fal fa-users"></i>
            <span
              >أخذها سائق آخر:
              <strong>{{
                summary.total_taken_by_other_driver_orders_count || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_orange"
            title="الترشيحات المغلقة بسبب الإلغاء أو إعادة البحث"
          >
            <i class="fal fa-times-circle"></i>
            <span
              >ترشيحات مغلقة:
              <strong>{{
                summary.total_closed_orders_count || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_blue"
            title="الترشيحات المنشأة في يوم التقرير وما زالت معلّقة"
          >
            <i class="fal fa-clock"></i>
            <span
              >ترشيحات معلّقة:
              <strong>{{
                summary.total_pending_orders_count || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_pink"
            title="إجمالي دقائق تأخير الطلبات (تجاوز حدود التوصيل)"
          >
            <i class="fal fa-hourglass-half"></i>
            <span
              >إجمالي تأخير الطلبات:
              <strong
                >{{ summary.order_delay_minutes_total || 0 }} د</strong
              ></span
            >
          </div>
          <div
            class="summary_mini_pill pill_pink"
            title="عدد الطلبات المتأخرة عن حدود التوصيل"
          >
            <i class="fal fa-box-open"></i>
            <span
              >طلبات متأخرة:
              <strong>{{
                summary.delayed_orders_count_total || 0
              }}</strong></span
            >
          </div>
          <div
            class="summary_mini_pill pill_pink"
            title="متوسط نسبة تأخير الطلبات عن حدود التوصيل"
          >
            <i class="fal fa-percentage"></i>
            <span
              >متوسط نسبة تأخير الطلبات:
              <strong
                >{{ formatPercentage(summary.order_delay_percentage_avg) }}%
              </strong></span
            >
          </div>
        </div>
      </div>

      <!-- Start Filters Section -->
      <div class="ops_filters_box p-3 mb-4 rounded">
        <div class="row align-items-end g-3">
          <!-- From Date -->
          <div class="col-lg-2 col-md-6">
            <div class="input_wrapper top_label mb-0">
              <label class="form-label">من تاريخ</label>
              <input
                type="date"
                class="form-control"
                v-model="filters.from"
                @change="onDateFilterChange"
              />
            </div>
          </div>

          <!-- To Date -->
          <div class="col-lg-2 col-md-6">
            <div class="input_wrapper top_label mb-0">
              <label class="form-label">إلى تاريخ</label>
              <input
                type="date"
                class="form-control"
                v-model="filters.to"
                @change="onDateFilterChange"
              />
            </div>
          </div>

          <!-- Driver Filter -->
          <div class="col-lg-3 col-md-6">
            <div class="input_wrapper top_label mb-0">
              <label class="form-label">تحديد المندوب</label>
              <multiselect
                v-model="filters.selected_driver"
                :options="driversList"
                label="full_name"
                track-by="id"
                placeholder="جميع المندوبين"
                :searchable="true"
                :allow-empty="true"
                :show-labels="false"
                @input="onDriverSelect"
              ></multiselect>
            </div>
          </div>

          <!-- Has Delay Filter -->
          <div class="col-lg-2 col-md-6">
            <div class="input_wrapper top_label mb-0">
              <label class="form-label">حالة التأخير</label>
              <multiselect
                v-model="filters.has_delay_obj"
                :options="delayOptions"
                label="name"
                track-by="value"
                placeholder="الكل"
                :searchable="false"
                :allow-empty="true"
                :show-labels="false"
                @input="
                  filters.has_delay = filters.has_delay_obj
                    ? filters.has_delay_obj.value
                    : null;
                  onFilterChange();
                "
              ></multiselect>
            </div>
          </div>

          <!-- Has Wallet Lock Filter -->
          <div class="col-lg-2 col-md-6">
            <div class="input_wrapper top_label mb-0">
              <label class="form-label">إغلاق المحفظة</label>
              <multiselect
                v-model="filters.has_wallet_lock_obj"
                :options="walletLockOptions"
                label="name"
                track-by="value"
                placeholder="الكل"
                :searchable="false"
                :allow-empty="true"
                :show-labels="false"
                @input="
                  filters.has_wallet_lock = filters.has_wallet_lock_obj
                    ? filters.has_wallet_lock_obj.value
                    : null;
                  onFilterChange();
                "
              ></multiselect>
            </div>
          </div>

          <!-- Filter Action Buttons -->
          <div class="col-lg-1 col-md-12 d-flex align-items-center gap-2">
            <button
              class="btn btn-primary w-100 d-flex align-items-center justify-content-center"
              @click="onFilterChange"
              title="تطبيق التصفية"
              style="height: 48px; background: #783d73; border-color: #783d73"
            >
              <i class="fal fa-filter"></i>
            </button>
            <button
              class="btn btn-outline-secondary d-flex align-items-center justify-content-center"
              @click="resetFilters"
              title="إعادة تعيين"
              style="height: 48px"
            >
              <i class="fal fa-undo"></i>
            </button>
          </div>
        </div>
      </div>
      <!-- End Filters Section -->

      <!-- Start Main Table -->
      <main class="magic-table-wrapper">
        <v-data-table
          class="thumb strip creative-data-table custom_report_table"
          :headers="headers"
          :items="tableItems"
          :loading="loading"
          :loading-text="$t('table.loadingData')"
          item-key="_rowKey"
          hide-default-footer
          :items-per-page="rows.length > 0 ? rows.length : per_page"
        >
          <!-- No Data -->
          <template v-slot:no-data>
            <div class="text-center py-5">
              <i
                class="fal fa-chart-line text-muted"
                style="font-size: 32px"
              ></i>
              <p class="mt-2 text-muted">{{ $t("table.noData") }}</p>
            </div>
          </template>

          <!-- Index -->
          <template v-slot:[`item.index`]="{ index }">
            <span class="index_badge"
              >#{{ (page - 1) * per_page + index + 1 }}</span
            >
          </template>

          <!-- Driver Info -->
          <template v-slot:[`item.driver`]="{ item }">
            <div class="d-flex align-items-center gap-2 py-1">
              <img
                :src="getDriverImage(item)"
                class="rounded-circle border"
                style="width: 38px; height: 38px; object-fit: cover"
                @error="
                  $event.target.src = require('@/assets/images/icons/logo.png')
                "
              />
              <div class="d-flex flex-column text-right">
                <router-link
                  :to="`/driver/show/${getDriverId(item)}`"
                  class="driver_link font-weight-bold"
                  style="color: #783d73; font-size: 13.5px"
                >
                  {{ getDriverName(item) }}
                </router-link>
                <span
                  class="text-muted"
                  style="font-size: 11.5px"
                  v-if="getDriverPhone(item)"
                >
                  {{ getDriverPhone(item) }}
                </span>
              </div>
            </div>
          </template>

          <!-- Report Date -->
          <template v-slot:[`item.report_date`]="{ item }">
            <span
              class="badge bg-light text-dark border px-2 py-1"
              style="font-size: 12px"
            >
              <i class="fal fa-calendar-alt ml-1 text-primary"></i>
              {{ item.report_date || item.date || "-" }}
            </span>
          </template>

          <!-- Scheduled Working / Shift Time -->
          <template v-slot:[`item.shift_time`]="{ item }">
            <div
              v-if="
                item.shift_start_at ||
                item.scheduled_start_at ||
                item.shift_end_at ||
                item.scheduled_end_at
              "
              class="d-flex flex-column"
              style="font-size: 12px"
            >
              <span class="text-dark font-weight-bold">
                {{
                  formatTimeOnly(item.shift_start_at || item.scheduled_start_at)
                }}
                -
                {{ formatTimeOnly(item.shift_end_at || item.scheduled_end_at) }}
              </span>
            </div>
            <span v-else class="text-muted" style="font-size: 12px"
              >غير مجدول</span
            >
          </template>

          <template v-slot:[`item.scheduled_time`]="{ item }">
            <div
              v-if="
                item.shift_start_at ||
                item.scheduled_start_at ||
                item.shift_end_at ||
                item.scheduled_end_at
              "
              class="d-flex flex-column"
              style="font-size: 12px"
            >
              <span class="text-dark font-weight-bold">
                {{
                  formatTimeOnly(item.shift_start_at || item.scheduled_start_at)
                }}
                -
                {{ formatTimeOnly(item.shift_end_at || item.scheduled_end_at) }}
              </span>
            </div>
            <span v-else class="text-muted" style="font-size: 12px"
              >غير مجدول</span
            >
          </template>

          <!-- Actual Working Time -->
          <template v-slot:[`item.actual_time`]="{ item }">
            <div
              v-if="item.actual_open_at || item.actual_close_at"
              class="d-flex flex-column"
              style="font-size: 12px"
            >
              <span class="text-success font-weight-bold">
                {{ formatTimeOnly(item.actual_open_at) }} -
                {{
                  item.actual_close_at
                    ? formatTimeOnly(item.actual_close_at)
                    : "مستمر"
                }}
              </span>
            </div>
            <span v-else class="text-muted" style="font-size: 12px"
              >لم يعمل</span
            >
          </template>

          <!-- Inside Shift / Working Time -->
          <template v-slot:[`item.inside_shift_work_minutes`]="{ item }">
            <span class="metric_chip chip_blue">
              {{
                formatMinutesToHHMM(
                  item.inside_shift_work_minutes !== undefined &&
                    item.inside_shift_work_minutes !== null
                    ? item.inside_shift_work_minutes
                    : item.inside_working_time_minutes
                )
              }}
            </span>
          </template>
          <template v-slot:[`item.inside_working_time_minutes`]="{ item }">
            <span class="metric_chip chip_blue">
              {{
                formatMinutesToHHMM(
                  item.inside_shift_work_minutes !== undefined &&
                    item.inside_shift_work_minutes !== null
                    ? item.inside_shift_work_minutes
                    : item.inside_working_time_minutes
                )
              }}
            </span>
          </template>

          <!-- Outside Shift / Working Time -->
          <template v-slot:[`item.outside_shift_work_minutes`]="{ item }">
            <span
              class="metric_chip"
              :class="
                (item.outside_shift_work_minutes !== undefined &&
                item.outside_shift_work_minutes !== null
                  ? item.outside_shift_work_minutes
                  : item.outside_working_time_minutes) > 0
                  ? 'chip_purple'
                  : 'chip_gray'
              "
            >
              {{
                formatMinutesToHHMM(
                  item.outside_shift_work_minutes !== undefined &&
                    item.outside_shift_work_minutes !== null
                    ? item.outside_shift_work_minutes
                    : item.outside_working_time_minutes
                )
              }}
            </span>
          </template>
          <template v-slot:[`item.outside_working_time_minutes`]="{ item }">
            <span
              class="metric_chip"
              :class="
                (item.outside_shift_work_minutes !== undefined &&
                item.outside_shift_work_minutes !== null
                  ? item.outside_shift_work_minutes
                  : item.outside_working_time_minutes) > 0
                  ? 'chip_purple'
                  : 'chip_gray'
              "
            >
              {{
                formatMinutesToHHMM(
                  item.outside_shift_work_minutes !== undefined &&
                    item.outside_shift_work_minutes !== null
                    ? item.outside_shift_work_minutes
                    : item.outside_working_time_minutes
                )
              }}
            </span>
          </template>

          <!-- Inside Zone Minutes -->
          <template v-slot:[`item.inside_zone_minutes`]="{ item }">
            <span class="metric_chip chip_green">
              {{ formatMinutesToHHMM(item.inside_zone_minutes) }}
            </span>
          </template>

          <!-- Outside Zone Minutes -->
          <template v-slot:[`item.outside_zone_minutes`]="{ item }">
            <span
              class="metric_chip"
              :class="
                item.outside_zone_minutes > 0 ? 'chip_amber' : 'chip_gray'
              "
            >
              {{ formatMinutesToHHMM(item.outside_zone_minutes) }}
            </span>
          </template>

          <!-- Delay Minutes -->
          <template v-slot:[`item.delay_minutes`]="{ item }">
            <span
              class="metric_chip"
              :class="item.delay_minutes > 0 ? 'chip_red' : 'chip_gray'"
            >
              {{ formatMinutesToHHMM(item.delay_minutes) }}
            </span>
          </template>

          <!-- Wallet Locked Excluded -->
          <template v-slot:[`item.wallet_locked_excluded_minutes`]="{ item }">
            <span
              class="metric_chip"
              :class="
                item.wallet_locked_excluded_minutes > 0
                  ? 'chip_orange'
                  : 'chip_gray'
              "
            >
              {{ formatMinutesToHHMM(item.wallet_locked_excluded_minutes) }}
            </span>
          </template>

          <!-- Approved Work Minutes -->
          <template v-slot:[`item.approved_work_minutes`]="{ item }">
            <span class="metric_chip chip_highlight">
              <i class="fal fa-check ml-1"></i>
              {{ formatMinutesToHHMM(item.approved_work_minutes) }}
            </span>
          </template>

          <!-- On-Time Adherence Rate -->
          <template v-slot:[`item.on_time_adherence_rate`]="{ item }">
            <span>{{ formatAdherenceRate(item.on_time_adherence_rate) }}</span>
          </template>
          
          <!-- Order Delivery Delay (distinct from shift lateness "delay_minutes") -->
          <template v-slot:[`item.order_delay`]="{ item }">
            <div
              class="d-flex flex-column align-items-center py-2"
              title="تأخير الطلبات عن حدود التوصيل المحددة في الإعدادات، لا علاقة له بتأخير بداية الدوام"
            >
              <span
                class="metric_chip mb-1"
                :class="item.order_delay_minutes > 0 ? 'chip_red' : 'chip_gray'"
              >
                {{ formatMinutesToHHMM(item.order_delay_minutes) }}
              </span>
              <span
                class="badge bg-light text-dark border px-2 py-1"
                style="font-size: 11px; border-radius: 6px"
              >
                {{ item.delayed_orders_count || 0 }} طلب متأخر ·
                {{ formatPercentage(item.order_delay_percentage) }}%
              </span>
            </div>
          </template>

          <!-- Orders & Acceptance Rate -->
          <template v-slot:[`item.orders_and_acceptance`]="{ item }">
            <div
              class="orders_acceptance_col d-flex flex-column align-items-center py-2"
            >
              <!-- Top Row: Acceptance Rate & Executed Orders & Quick Details -->
              <div class="d-flex align-items-center gap-1 mb-1">
                <span
                  class="badge px-2 py-1 font-weight-bold"
                  :class="getAcceptanceBadgeClass(item.acceptance_rate)"
                  style="font-size: 12px; border-radius: 6px"
                  title="الطلبات المقبولة / (الطلبات المقبولة + الطلبات المرفوضة يدويًا + الطلبات المنتهية بدون قبول)"
                >
                  {{ formatPercentage(item.acceptance_rate) }}%
                </span>
                <span
                  class="badge bg-light text-dark border px-2 py-1"
                  style="font-size: 11px; border-radius: 6px"
                  title="الطلبات المقبولة / (الطلبات المقبولة + الطلبات المرفوضة يدويًا + الطلبات المنتهية بدون قبول)"
                >
                  <i class="fal fa-box ml-1 text-primary"></i>
                  {{ item.orders_count }} طلب تم توصيله
                  <span
                  class="nom_chip"
                  :class="
                    (item.assigned_orders_count || 0) > 0
                      ? 'nom_chip_purple'
                      : 'nom_chip_muted'
                  "
                  title="الطلبات المسندة إداريًا"
                >
                  <i class="fal fa-user-tag ml-1"></i>مسندة:
                  <strong>{{ item.assigned_orders_count || 0 }}</strong>
                </span>
                </span>

                <!-- Quick Details Popover/Menu -->
                <v-menu
                  offset-y
                  open-on-hover
                  left
                  :close-on-content-click="false"
                  nudge-bottom="5"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <button
                      type="button"
                      class="btn btn-sm btn-light p-0 d-inline-flex align-items-center justify-content-center border rounded-circle"
                      style="
                        width: 20px;
                        height: 20px;
                        font-size: 10px;
                        color: #783d73;
                        cursor: pointer;
                      "
                      v-bind="attrs"
                      v-on="on"
                      title="تفاصيل حالات ترشيحات الطلبات"
                    >
                      <i class="fal fa-info-circle"></i>
                    </button>
                  </template>
                  <div
                    class="p-3 bg-white rounded-lg shadow-lg border"
                    style="min-width: 270px; font-size: 12px; z-index: 100"
                  >
                    <div
                      class="font-weight-bold pb-2 mb-2 border-bottom d-flex align-items-center justify-content-between text-dark"
                    >
                      <span
                        ><i class="fal fa-chart-pie ml-1 text-primary"></i>
                        تفاصيل الترشيحات والطلبات</span
                      >
                      <span class="badge badge-primary"
                        >{{ formatPercentage(item.acceptance_rate) }}%</span
                      >
                    </div>
                    <div class="d-flex flex-column gap-1 text-muted">
                      <div class="font-weight-bold text-dark mb-1">
                        <i class="fal fa-box ml-1 text-primary"></i> الطلبات
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>الطلبات المقبولة:</span>
                        <strong class="text-success">{{
                          item.accepted_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>الطلبات المسندة إداريًا:</span>
                        <strong class="text-purple">{{
                          item.assigned_orders_count || 0
                        }}</strong>
                      </div>

                      <div class="font-weight-bold text-dark mt-3 mb-1">
                        <i class="fal fa-user-check ml-1 text-primary"></i>
                        الترشيحات
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>الترشيحات المنتهية المهلة:</span>
                        <strong class="text-danger">{{
                          item.expired_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>الترشيحات التي أخذها سائق آخر:</span>
                        <strong class="text-amber">{{
                          item.taken_by_other_driver_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>الترشيحات المعلّقة:</span>
                        <strong class="text-cyan">{{
                          item.pending_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                        v-if="item.not_accepted_orders_count !== undefined"
                      >
                        <span>غير مقبولة (متروكة):</span>
                        <strong class="text-danger">{{
                          item.not_accepted_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between py-1 border-bottom"
                        v-if="item.manual_rejected_orders_count > 0"
                      >
                        <span>مرفوضة يدويًا:</span>
                        <strong class="text-danger">{{
                          item.manual_rejected_orders_count
                        }}</strong>
                      </div>
                                            <div
                        class="d-flex justify-content-between py-1 border-bottom"
                      >
                        <span>المغلقة (إلغاء أو إعادة بحث):</span>
                        <strong class="text-orange">{{
                          item.closed_orders_count || 0
                        }}</strong>
                      </div>
                      <div
                        class="d-flex justify-content-between pt-1 font-weight-bold text-dark"
                      >
                        <span>إجمالي الترشيحات المعروضة:</span>
                        <span>{{ item.offered_orders_count || 0 }}</span>
                      </div>
                    </div>
                  </div>
                </v-menu>
              </div>


              <hr class="border-primary my-2">
              <h6 class="text-center mb-2">
                  <i class="fal fa-chart-pie text-primary"></i>
                  تفاصيل الترشيحات
              </h6>
              <!-- Main Orders Numbers: Accepted / Offered -->
              <div
                class="text-muted text-center mb-1"
                style="font-size: 11.5px; line-height: 1.3"
                v-if="
                  item.offered_orders_count !== undefined ||
                  item.accepted_orders_count !== undefined
                "
              >
                <span
                  >مقبول:
                  <strong class="text-dark">{{
                    item.accepted_orders_count || 0
                  }}</strong>
                  / معروض:
                  <strong class="text-dark">{{
                    item.offered_orders_count || 0
                  }}</strong></span
                >
              </div>

              <!-- All Nominations Breakdown Chips in this Column -->
              <div
                class="nominations_cell_chips d-flex flex-wrap justify-content-center gap-1"
                style="max-width: 260px"
              >

                <span
                  class="nom_chip"
                  :class="
                    (item.expired_orders_count || 0) > 0
                      ? 'nom_chip_red'
                      : 'nom_chip_muted'
                  "
                  title="الترشيحات المنتهية المهلة"
                >
                  <i class="fal fa-hourglass-end ml-1"></i>منتهية:
                  <strong>{{ item.expired_orders_count || 0 }}</strong>
                </span>

                <span
                  class="nom_chip"
                  :class="
                    (item.taken_by_other_driver_orders_count || 0) > 0
                      ? 'nom_chip_amber'
                      : 'nom_chip_muted'
                  "
                  title="الترشيحات التي أخذها سائق آخر"
                >
                  <i class="fal fa-users ml-1"></i>أخذها آخر:
                  <strong>{{
                    item.taken_by_other_driver_orders_count || 0
                  }}</strong>
                </span>

                <span
                  class="nom_chip"
                  :class="
                    (item.closed_orders_count || 0) > 0
                      ? 'nom_chip_orange'
                      : 'nom_chip_muted'
                  "
                  title="الترشيحات المغلقة بسبب الإلغاء أو إعادة البحث"
                >
                  <i class="fal fa-times-circle ml-1"></i>مغلقة:
                  <strong>{{ item.closed_orders_count || 0 }}</strong>
                </span>

                <span
                  class="nom_chip"
                  :class="
                    (item.pending_orders_count || 0) > 0
                      ? 'nom_chip_blue'
                      : 'nom_chip_muted'
                  "
                  title="الترشيحات المنشأة في يوم التقرير وما زالت معلّقة"
                >
                  <i class="fal fa-clock ml-1"></i>معلّقة:
                  <strong>{{ item.pending_orders_count || 0 }}</strong>
                </span>

                <span
                  class="nom_chip nom_chip_muted"
                  v-if="item.not_accepted_orders_count > 0"
                  title="طلبات لم تقبل (متروكة)"
                >
                  متروكة: <strong>{{ item.not_accepted_orders_count }}</strong>
                </span>

                <span
                  class="nom_chip nom_chip_red"
                  v-if="item.manual_rejected_orders_count > 0"
                  title="طلبات مرفوضة يدويًا"
                >
                  مرفوضة:
                  <strong>{{ item.manual_rejected_orders_count }}</strong>
                </span>
              </div>
            </div>
          </template>

          <!-- Actions -->
          <template v-slot:[`item.actions`]="{ item }">
            <div
              class="d-flex align-items-center justify-content-center gap-2 flex-nowrap text-nowrap"
            >
              <button
                class="btn btn-sm btn-outline-warning d-inline-flex align-items-center gap-1 text-nowrap action_btn"
                @click="rebuildDriverReport(item)"
                :disabled="rebuildingDriverId === getDriverId(item)"
                title="إعادة بناء التقرير لهذا المندوب"
              >
                <i
                  class="fal fa-sync-alt"
                  :class="{
                    'fa-spin': rebuildingDriverId === getDriverId(item),
                  }"
                ></i>
                <span class="text-nowrap">إعادة بناء</span>
              </button>
              <button
                class="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 text-nowrap action_btn action_btn_details"
                @click="openDetailsModal(item)"
                title="عرض التفاصيل والجلسات"
              >
                <i class="fal fa-eye"></i>
                <span class="text-nowrap">التفاصيل</span>
              </button>
            </div>
          </template>
        </v-data-table>

        <!-- Start Creative Pagination & Control Toolbar -->
        <div
          class="creative_pagination_toolbar mt-4"
          v-if="rows.length > 0 || totalRows > 0"
        >
          <div
            class="inner_toolbar_wrapper d-flex align-items-center justify-content-between flex-wrap gap-3"
          >
            <!-- Part 1: Record Count & Scope Tracker -->
            <div class="tracker_group d-flex align-items-center gap-3">
              <div class="tracker_icon_box">
                <i class="fal fa-database"></i>
              </div>
              <div class="tracker_info">
                <div class="d-flex align-items-center gap-2">
                  <span class="tracker_label">إجمالي السجلات:</span>
                  <span class="total_badge">{{ totalRows }}</span>
                </div>
                <div class="tracker_sub">
                  عرض السجلات
                  <strong class="text-primary">{{ fromRecord }}</strong> إلى
                  <strong class="text-primary">{{ toRecord }}</strong>
                </div>
              </div>
            </div>

            <!-- Part 2: Interactive Per-Page Segmented Control -->
            <div
              class="per_page_interactive_group d-flex align-items-center gap-2"
            >
              <span class="per_page_title d-flex align-items-center gap-1">
                <i class="fal fa-layer-group"></i>
                <span>لكل صفحة:</span>
              </span>

              <!-- Segmented Pills for modern quick switching -->
              <div class="segmented_pills_container">
                <button
                  v-for="opt in perPageOptions"
                  :key="opt"
                  type="button"
                  class="seg_pill_btn"
                  :class="{ active: per_page === opt }"
                  @click="selectPerPage(opt)"
                  :title="`عرض ${opt} سجل في كل صفحة`"
                >
                  {{ opt }}
                </button>
              </div>
            </div>

            <!-- Part 3: Navigation & Page Pills -->
            <div class="pagination_nav_section d-flex align-items-center gap-2">
              <template v-if="totalPages > 1">
                <v-pagination
                  v-model.number="page"
                  :length="totalPages"
                  :total-visible="5"
                  color="#783d73"
                  @input="fetchData($event)"
                  class="custom_v_pagination"
                ></v-pagination>
              </template>
              <div v-else-if="totalRows > 0" class="single_page_badge">
                <i class="fal fa-check-circle ml-1"></i>
                <span>الصفحة 1 من 1</span>
              </div>
            </div>
          </div>
        </div>
        <!-- End Creative Pagination & Control Toolbar -->
      </main>
      <!-- End Main Table -->
    </div>

    <!-- Start Details Modal Dialog -->
    <v-dialog v-model="detailsModal.open" max-width="1050px" scrollable>
      <v-card
        class="details_dialog_card rounded-xl overflow-hidden border-0 shadow-lg"
      >
        <!-- Creative Hero Header -->
        <div class="modal_hero_header p-4 text-white">
          <div
            class="d-flex align-items-center justify-content-between flex-wrap gap-3"
          >
            <!-- Driver Hero Info -->
            <div class="d-flex align-items-center gap-3">
              <div class="hero_avatar_wrapper">
                <img
                  :src="getDriverImage(detailsModal.driver)"
                  class="hero_avatar_img"
                  @error="
                    $event.target.src = require('@/assets/images/icons/logo.png')
                  "
                />
                <span class="avatar_badge_icon">
                  <i class="fal fa-user-shield"></i>
                </span>
              </div>
              <div>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <h4 class="mb-0 font-weight-bold text-white hero_title">
                    {{ getDriverName(detailsModal.driver) }}
                  </h4>
                  <span
                    class="badge hero_badge_id"
                    v-if="getDriverId(detailsModal.driver)"
                  >
                    #{{ getDriverId(detailsModal.driver) }}
                  </span>
                </div>
                <div
                  class="d-flex align-items-center gap-3 flex-wrap mt-1 hero_sub_info"
                >
                  <span class="hero_sub_item">
                    <i class="fal fa-calendar-day text-warning ml-1"></i>
                    {{ detailsModal.date }}
                  </span>
                  <span
                    class="hero_sub_item"
                    v-if="getDriverPhone(detailsModal.driver)"
                  >
                    <i class="fal fa-phone-alt text-info ml-1"></i>
                    {{ getDriverPhone(detailsModal.driver) }}
                  </span>
                  <span class="hero_sub_item">
                    <i class="fal fa-chart-network text-success ml-1"></i>
                    سجل العمليات والجلسات
                  </span>
                </div>
              </div>
            </div>

            <!-- Header Actions -->
            <div class="d-flex align-items-center gap-2">
              <button
                class="btn btn-sm btn-hero-action d-flex align-items-center gap-1"
                @click="rebuildDriverReport(detailsModal.driver)"
                :disabled="rebuilding || detailsModal.loading"
                title="إعادة بناء التقرير لهذا المندوب"
              >
                <i
                  class="fal fa-sync-alt"
                  :class="{ 'fa-spin': rebuilding }"
                ></i>
                <span>إعادة بناء التقرير</span>
              </button>
              <button
                class="btn btn-icon-close"
                @click="detailsModal.open = false"
                title="إغلاق"
              >
                <i class="fal fa-times"></i>
              </button>
            </div>
          </div>
        </div>

        <!-- Modal Body Content -->
        <v-card-text class="p-4" style="max-height: 75vh; background: #f8fafc">
          <div v-if="detailsModal.loading" class="text-center py-5">
            <div class="modal_loader_box">
              <v-progress-circular
                indeterminate
                color="#783d73"
                size="54"
                width="4"
              ></v-progress-circular>
              <h6 class="mt-3 font-weight-bold text-dark">
                جاري استخراج تفاصيل الجلسات والعمليات...
              </h6>
              <p class="text-muted small mb-0">
                يتم الآن جلب جلسات الإتاحة، التواجد بالزون، سجلات التأخير وفترات الطلبات
              </p>
            </div>
          </div>

          <div v-else>
            <!-- Creative KPI Dashboard Grid -->
            <div class="modal_kpi_dashboard mb-4" v-if="modalActiveReport">
              <div class="row g-3">
                <!-- KPI 1: Approved Work Time -->
                <div class="col-lg-3 col-sm-6">
                  <div class="kpi_stat_card kpi_card_purple">
                    <div class="kpi_icon_wrapper">
                      <i class="fal fa-award"></i>
                    </div>
                    <div class="kpi_content">
                      <span class="kpi_label">العمل المعتمد</span>
                      <h4 class="kpi_value text-purple">
                        {{
                          formatMinutesToHHMM(
                            modalActiveReport.approved_work_minutes
                          )
                        }}
                      </h4>
                      <span class="kpi_sub">ساعات عمل محتسبة</span>
                    </div>
                  </div>
                </div>

                <!-- KPI 2: Shift vs Actual Hours -->
                <div class="col-lg-3 col-sm-6">
                  <div class="kpi_stat_card kpi_card_blue">
                    <div class="kpi_icon_wrapper">
                      <i class="fal fa-user-clock"></i>
                    </div>
                    <div class="kpi_content">
                      <span class="kpi_label">الشفت vs الفعلي</span>
                      <div class="kpi_dual_row">
                        <span class="dual_item">
                          <small>شفت:</small>
                          <strong
                            >{{
                              formatTimeOnly(
                                modalActiveReport.shift_start_at ||
                                  modalActiveReport.scheduled_start_at
                              )
                            }}
                            -
                            {{
                              formatTimeOnly(
                                modalActiveReport.shift_end_at ||
                                  modalActiveReport.scheduled_end_at
                              )
                            }}</strong
                          >
                        </span>
                        <span class="dual_item text-success">
                          <small>فعلي:</small>
                          <strong
                            >{{
                              formatTimeOnly(modalActiveReport.actual_open_at)
                            }}
                            -
                            {{
                              modalActiveReport.actual_close_at
                                ? formatTimeOnly(
                                    modalActiveReport.actual_close_at
                                  )
                                : "مستمر"
                            }}</strong
                          >
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- KPI 3: Coverage & Compliance -->
                <div class="col-lg-3 col-sm-6">
                  <div class="kpi_stat_card kpi_card_green">
                    <div class="kpi_icon_wrapper">
                      <i class="fal fa-map-marked-alt"></i>
                    </div>
                    <div class="kpi_content">
                      <span class="kpi_label">التغطية والزون</span>
                      <div class="kpi_dual_row">
                        <span class="dual_item">
                          <small>داخل الشفت:</small>
                          <strong class="text-primary">{{
                            formatMinutesToHHMM(
                              modalActiveReport.inside_shift_work_minutes ??
                                modalActiveReport.inside_working_time_minutes
                            )
                          }}</strong>
                        </span>
                        <span class="dual_item">
                          <small>داخل الزون:</small>
                          <strong class="text-success">{{
                            formatMinutesToHHMM(
                              modalActiveReport.inside_zone_minutes
                            )
                          }}</strong>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- KPI 4: Orders & Acceptance -->
                <div class="col-lg-3 col-sm-6">
                  <div class="kpi_stat_card kpi_card_amber">
                    <div class="kpi_icon_wrapper">
                      <i class="fal fa-percentage"></i>
                    </div>
                    <div class="kpi_content">
                      <div
                        class="d-flex align-items-center justify-content-between"
                      >
                        <span class="kpi_label">نسبة القبول</span>
                        <span
                          class="badge kpi_rate_badge"
                          :class="
                            getAcceptanceBadgeClass(
                              modalActiveReport.acceptance_rate
                            )
                          "
                        >
                          {{
                            formatPercentage(modalActiveReport.acceptance_rate)
                          }}%
                        </span>
                      </div>
                      <div class="kpi_orders_strip mt-1">
                        <span
                          >منفذ:
                          <strong>{{
                            modalActiveReport.orders_count || 0
                          }}</strong></span
                        >
                        <span
                          >معروض:
                          <strong>{{
                            modalActiveReport.offered_orders_count || 0
                          }}</strong></span
                        >
                        <span
                          >مقبول:
                          <strong>{{
                            modalActiveReport.accepted_orders_count || 0
                          }}</strong></span
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Nominations & Orders Detailed Breakdown -->
              <div
                class="nominations_breakdown_box p-3 mt-3 rounded-xl shadow-sm"
                v-if="modalActiveReport"
              >
                <div
                  class="d-flex align-items-center justify-content-between mb-2 flex-wrap gap-2"
                >
                  <div class="d-flex align-items-center gap-2">
                    <div class="breakdown_icon_badge">
                      <i class="fal fa-chart-pie text-white"></i>
                    </div>
                    <div>
                      <h6
                        class="mb-0 font-weight-bold"
                        style="font-size: 13.5px; color: #1e293b"
                      >
                        تفاصيل ترشيحات وطلبات اليوم
                      </h6>
                      <span class="text-muted" style="font-size: 11px">
                        توزيع تفصيلي لحالات الترشيحات والقبول والإسناد الإداري
                      </span>
                    </div>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span
                      class="badge px-2 py-1 font-weight-bold"
                      :class="
                        getAcceptanceBadgeClass(
                          modalActiveReport.acceptance_rate
                        )
                      "
                      style="font-size: 11.5px; border-radius: 6px"
                    >
                      نسبة القبول:
                      {{ formatPercentage(modalActiveReport.acceptance_rate) }}%
                    </span>
                  </div>
                </div>

                <div class="row g-2 text-center">
                  <!-- Accepted Orders -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_accepted">
                      <span class="nom_stat_title">الطلبات المقبولة</span>
                      <h4 class="nom_stat_num text-success">
                        {{ modalActiveReport.accepted_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">تم قبولها بواسطة المندوب</span>
                    </div>
                  </div>

                  <!-- Assigned Orders -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_assigned">
                      <span class="nom_stat_title"
                        >الطلبات المسندة إداريًا</span
                      >
                      <h4 class="nom_stat_num text-purple">
                        {{ modalActiveReport.assigned_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">إسناد مباشر من الإدارة</span>
                    </div>
                  </div>

                  <!-- Expired Orders -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_expired">
                      <span class="nom_stat_title"
                        >الترشيحات المنتهية المهلة</span
                      >
                      <h4 class="nom_stat_num text-danger">
                        {{ modalActiveReport.expired_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">انتهى وقت قبول الترشيح</span>
                    </div>
                  </div>

                  <!-- Taken by Other Driver -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_taken">
                      <span class="nom_stat_title">أخذها سائق آخر</span>
                      <h4 class="nom_stat_num text-amber">
                        {{
                          modalActiveReport.taken_by_other_driver_orders_count ||
                          0
                        }}
                      </h4>
                      <span class="nom_stat_sub">قبلها مندوب آخر</span>
                    </div>
                  </div>

                  <!-- Closed Orders -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_closed">
                      <span class="nom_stat_title">الترشيحات المغلقة</span>
                      <h4 class="nom_stat_num text-orange">
                        {{ modalActiveReport.closed_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">إلغاء أو إعادة البحث</span>
                    </div>
                  </div>

                  <!-- Pending Orders -->
                  <div class="col-lg col-md-4 col-6">
                    <div class="nom_stat_card card_pending">
                      <span class="nom_stat_title">الترشيحات المعلّقة</span>
                      <h4 class="nom_stat_num text-cyan">
                        {{ modalActiveReport.pending_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">منشأة وما زالت معلّقة</span>
                    </div>
                  </div>

                  <!-- Not Accepted Orders -->
                  <div
                    class="col-lg col-md-4 col-6"
                    v-if="
                      modalActiveReport.not_accepted_orders_count !== undefined
                    "
                  >
                    <div class="nom_stat_card card_not_accepted">
                      <span class="nom_stat_title">طلبات غير مقبولة</span>
                      <h4 class="nom_stat_num text-muted">
                        {{ modalActiveReport.not_accepted_orders_count || 0 }}
                      </h4>
                      <span class="nom_stat_sub">لم يتم قبولها (متروكة)</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Secondary Quick Alerts Strip -->
              <div
                class="row g-2 mt-1"
                v-if="
                  modalActiveReport.delay_minutes > 0 ||
                  modalActiveReport.wallet_locked_excluded_minutes > 0 ||
                  modalActiveReport.outside_zone_minutes > 0 ||
                  modalActiveReport.order_delay_minutes > 0
                "
              >
                <div
                  class="col-md-4 col-sm-6"
                  v-if="modalActiveReport.order_delay_minutes > 0"
                >
                  <div
                    class="alert_mini_pill pill_red"
                    title="تأخير الطلبات عن حدود التوصيل، لا علاقة له بتأخير بداية الدوام"
                  >
                    <i class="fal fa-hourglass-half"></i>
                    <span
                      >تأخير الطلبات:
                      <strong>{{
                        formatMinutesToHHMM(
                          modalActiveReport.order_delay_minutes
                        )
                      }}</strong>
                      ({{ modalActiveReport.delayed_orders_count || 0 }} طلب ·
                      {{
                        formatPercentage(modalActiveReport.order_delay_percentage)
                      }}%)</span
                    >
                  </div>
                </div>
                <div
                  class="col-md-4 col-sm-6"
                  v-if="modalActiveReport.delay_minutes > 0"
                >
                  <div class="alert_mini_pill pill_red">
                    <i class="fal fa-exclamation-circle"></i>
                    <span
                      >تأخير عن بدء الشفت:
                      <strong>{{
                        formatMinutesToHHMM(modalActiveReport.delay_minutes)
                      }}</strong></span
                    >
                  </div>
                </div>
                <div
                  class="col-md-4 col-sm-6"
                  v-if="modalActiveReport.wallet_locked_excluded_minutes > 0"
                >
                  <div class="alert_mini_pill pill_orange">
                    <i class="fal fa-wallet"></i>
                    <span
                      >استبعاد قفل المحفظة:
                      <strong>{{
                        formatMinutesToHHMM(
                          modalActiveReport.wallet_locked_excluded_minutes
                        )
                      }}</strong></span
                    >
                  </div>
                </div>
                <div
                  class="col-md-4 col-sm-6"
                  v-if="modalActiveReport.outside_zone_minutes > 0"
                >
                  <div class="alert_mini_pill pill_amber">
                    <i class="fal fa-map-marker-slash"></i>
                    <span
                      >خارج نطاق الزون:
                      <strong>{{
                        formatMinutesToHHMM(
                          modalActiveReport.outside_zone_minutes
                        )
                      }}</strong></span
                    >
                  </div>
                </div>
              </div>
            </div>

            <!-- Creative Segmented Tabs Bar -->
            <div class="creative_tabs_wrapper mb-3">
              <v-tabs
                v-model="detailsModal.activeTab"
                class="creative_modal_tabs"
                grow
                show-arrows
                hide-slider
              >
                <!-- Tab 1: Activity Sessions -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-user-clock tab_icon icon_blue"></i>
                    <span class="tab_title">الإتاحة الفعلية</span>
                    <span class="tab_counter_chip chip_blue">
                      {{ detailsModal.data.activity_sessions?.length || 0 }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 2: Zone Sessions -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-map-marker-alt tab_icon icon_green"></i>
                    <span class="tab_title">الزون والتغطية</span>
                    <span class="tab_counter_chip chip_green">
                      {{ detailsModal.data.zone_sessions?.length || 0 }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 3: Wallet Lock -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-wallet tab_icon icon_orange"></i>
                    <span class="tab_title">إغلاق المحفظة</span>
                    <span class="tab_counter_chip chip_orange">
                      {{ detailsModal.data.wallet_lock_sessions?.length || 0 }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 4: Delays -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-history tab_icon icon_red"></i>
                    <span class="tab_title">سجلات التأخير</span>
                    <span class="tab_counter_chip chip_red">
                      {{ detailsModal.data.delay_records?.length || 0 }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 5: Active Order Intervals -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-boxes-alt tab_icon icon_teal"></i>
                    <span class="tab_title">فترات الطلبات</span>
                    <span class="tab_counter_chip chip_teal">
                      {{
                        detailsModal.data.active_order_intervals?.length || 0
                      }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 6: Multi-Day Reports -->
                <v-tab
                  v-if="
                    detailsModal.data.reports &&
                    detailsModal.data.reports.length > 1
                  "
                >
                  <div class="tab_inner_content">
                    <i class="fal fa-calendar-alt tab_icon icon_purple"></i>
                    <span class="tab_title">سجل الأيام</span>
                    <span class="tab_counter_chip chip_purple">
                      {{ detailsModal.data.reports.length }}
                    </span>
                  </div>
                </v-tab>

                <!-- Tab 6: Working Times -->
                <v-tab>
                  <div class="tab_inner_content">
                    <i class="fal fa-calendar-check tab_icon icon_indigo"></i>
                    <span class="tab_title">أوقات العمل</span>
                  </div>
                </v-tab>
              </v-tabs>
            </div>

            <!-- Tab Items Content -->
            <v-tabs-items
              v-model="detailsModal.activeTab"
              class="bg-transparent"
            >
              <!-- Tab 1: Activity Sessions -->
              <v-tab-item>
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 55px">#</th>
                          <th>بداية الجلسة</th>
                          <th>نهاية الجلسة</th>
                          <th>المدة المستغرقة</th>
                          <th>المصدر</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(session, sIdx) in detailsModal.data
                            .activity_sessions"
                          :key="sIdx"
                          class="session_row"
                        >
                          <td>
                            <span class="row_num_badge">{{ sIdx + 1 }}</span>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-primary"></i>
                                {{
                                  formatTimeOnly(
                                    session.started_at ||
                                      session.start_at ||
                                      session.from
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">
                                {{
                                  formatDateOnly(
                                    session.started_at ||
                                      session.start_at ||
                                      session.from
                                  )
                                }}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div
                              class="dt_cell"
                              v-if="
                                session.ended_at || session.end_at || session.to
                              "
                            >
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-secondary"></i>
                                {{
                                  formatTimeOnly(
                                    session.ended_at ||
                                      session.end_at ||
                                      session.to
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">
                                {{
                                  formatDateOnly(
                                    session.ended_at ||
                                      session.end_at ||
                                      session.to
                                  )
                                }}
                              </span>
                            </div>
                            <span v-else class="live_active_chip">
                              <span class="pulse_dot"></span>
                              مفتوحة حالياً
                            </span>
                          </td>
                          <td>
                            <span
                              class="duration_pill"
                              :class="
                                session.is_closed
                                  ? 'duration_closed'
                                  : 'duration_active'
                              "
                            >
                              <i class="fal fa-hourglass-half ml-1"></i>
                              {{
                                calculateDuration(
                                  session.started_at ||
                                    session.start_at ||
                                    session.from,
                                  session.ended_at ||
                                    session.end_at ||
                                    session.to
                                )
                              }}
                            </span>
                          </td>
                          <td>
                            <span class="source_tag">
                              <i
                                class="fal fa-mobile-android-alt ml-1 text-muted"
                              ></i>
                              {{ getReadableSource(session.source) }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="status_pill"
                              :class="
                                session.is_closed ? 'pill_closed' : 'pill_open'
                              "
                            >
                              <span
                                class="status_dot"
                                :class="
                                  session.is_closed ? 'dot_gray' : 'dot_pulse'
                                "
                              ></span>
                              {{ session.is_closed ? "مغلقة" : "مفتوحة" }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            !detailsModal.data.activity_sessions ||
                            !detailsModal.data.activity_sessions.length
                          "
                        >
                          <td colspan="6">
                            <div class="empty_tab_state py-5">
                              <div class="empty_state_icon bg_light_blue">
                                <i class="fal fa-user-clock text-primary"></i>
                              </div>
                              <h6 class="font-weight-bold mt-3 mb-1">
                                لا توجد جلسات إتاحة مسجلة
                              </h6>
                              <p class="text-muted small mb-0">
                                لم يتم تسجيل فتح للتطبيق أو فترات إتاحة لهذا
                                اليوم
                              </p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 2: Zone Sessions -->
              <v-tab-item>
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 55px">#</th>
                          <th>حالة التواجد</th>
                          <th>الحي / المنطقة</th>
                          <th>بداية الفترة</th>
                          <th>نهاية الفترة</th>
                          <th>المدة</th>
                          <th>حالة الجلسة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(zSession, zIdx) in detailsModal.data
                            .zone_sessions"
                          :key="zIdx"
                          class="session_row"
                        >
                          <td>
                            <span class="row_num_badge">{{ zIdx + 1 }}</span>
                          </td>
                          <td>
                            <span
                              class="zone_status_pill"
                              :class="
                                zSession.status === 'inside' ||
                                zSession.is_inside
                                  ? 'zone_inside'
                                  : 'zone_outside'
                              "
                            >
                              <i
                                :class="
                                  zSession.status === 'inside' ||
                                  zSession.is_inside
                                    ? 'fal fa-map-marker-check ml-1'
                                    : 'fal fa-map-marker-slash ml-1'
                                "
                              ></i>
                              {{
                                zSession.status === "inside" ||
                                zSession.is_inside
                                  ? "داخل الزون"
                                  : "خارج الزون"
                              }}
                            </span>
                          </td>
                          <td>
                            <span class="district_chip">
                              <i class="fal fa-map-pin ml-1 text-primary"></i>
                              {{
                                zSession.district?.name ||
                                zSession.district_name ||
                                (zSession.district_id
                                  ? "#" + zSession.district_id
                                  : zSession.zone_name || "المنطقة المخصصة")
                              }}
                            </span>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-primary"></i>
                                {{
                                  formatTimeOnly(
                                    zSession.started_at || zSession.start_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  zSession.started_at || zSession.start_at
                                )
                              }}</span>
                            </div>
                          </td>
                          <td>
                            <div
                              class="dt_cell"
                              v-if="zSession.ended_at || zSession.end_at"
                            >
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-secondary"></i>
                                {{
                                  formatTimeOnly(
                                    zSession.ended_at || zSession.end_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  zSession.ended_at || zSession.end_at
                                )
                              }}</span>
                            </div>
                            <span v-else class="live_active_chip">
                              <span class="pulse_dot"></span>
                              مستمرة
                            </span>
                          </td>
                          <td>
                            <span class="duration_pill duration_closed">
                              <i class="fal fa-hourglass-half ml-1"></i>
                              {{
                                calculateDuration(
                                  zSession.started_at || zSession.start_at,
                                  zSession.ended_at || zSession.end_at
                                )
                              }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="status_pill"
                              :class="
                                zSession.is_closed ? 'pill_closed' : 'pill_open'
                              "
                            >
                              <span
                                class="status_dot"
                                :class="
                                  zSession.is_closed ? 'dot_gray' : 'dot_pulse'
                                "
                              ></span>
                              {{ zSession.is_closed ? "مغلقة" : "مفتوحة" }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            !detailsModal.data.zone_sessions ||
                            !detailsModal.data.zone_sessions.length
                          "
                        >
                          <td colspan="7">
                            <div class="empty_tab_state py-5">
                              <div class="empty_state_icon bg_light_green">
                                <i
                                  class="fal fa-map-marked-alt text-success"
                                ></i>
                              </div>
                              <h6 class="font-weight-bold mt-3 mb-1">
                                لا توجد سجلات زون مسجلة
                              </h6>
                              <p class="text-muted small mb-0">
                                لم يتم رصد حركات انتقال داخل وخارج نطاق الزون
                              </p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 3: Wallet Lock Sessions -->
              <v-tab-item>
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 55px">#</th>
                          <th>وقت الإغلاق</th>
                          <th>وقت الفتح / الإتاحة</th>
                          <th>المدة المستبعدة</th>
                          <th>سبب القفل</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(wSession, wIdx) in detailsModal.data
                            .wallet_lock_sessions"
                          :key="wIdx"
                          class="session_row"
                        >
                          <td>
                            <span class="row_num_badge">{{ wIdx + 1 }}</span>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge text-danger">
                                <i class="fal fa-lock-alt ml-1 text-danger"></i>
                                {{
                                  formatTimeOnly(
                                    wSession.started_at ||
                                      wSession.start_at ||
                                      wSession.locked_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  wSession.started_at ||
                                    wSession.start_at ||
                                    wSession.locked_at
                                )
                              }}</span>
                            </div>
                          </td>
                          <td>
                            <div
                              class="dt_cell"
                              v-if="
                                wSession.ended_at ||
                                wSession.end_at ||
                                wSession.unlocked_at
                              "
                            >
                              <span class="dt_time_badge text-success">
                                <i
                                  class="fal fa-unlock-alt ml-1 text-success"
                                ></i>
                                {{
                                  formatTimeOnly(
                                    wSession.ended_at ||
                                      wSession.end_at ||
                                      wSession.unlocked_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  wSession.ended_at ||
                                    wSession.end_at ||
                                    wSession.unlocked_at
                                )
                              }}</span>
                            </div>
                            <span v-else class="live_alert_chip">
                              <span class="pulse_dot_red"></span>
                              مقفل حالياً
                            </span>
                          </td>
                          <td>
                            <span class="duration_pill duration_danger">
                              <i class="fal fa-stopwatch ml-1"></i>
                              {{
                                calculateDuration(
                                  wSession.started_at ||
                                    wSession.start_at ||
                                    wSession.locked_at,
                                  wSession.ended_at ||
                                    wSession.end_at ||
                                    wSession.unlocked_at
                                )
                              }}
                            </span>
                          </td>
                          <td>
                            <span class="reason_chip">
                              <i
                                class="fal fa-shield-exclamation text-danger ml-1"
                              ></i>
                              {{
                                wSession.lock_reason ||
                                wSession.reason ||
                                "رصيد غير مؤهل (Non-eligible)"
                              }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="status_pill"
                              :class="
                                wSession.is_closed
                                  ? 'pill_success'
                                  : 'pill_danger'
                              "
                            >
                              {{
                                wSession.is_closed
                                  ? "تمت الإتاحة"
                                  : "مقفل حالياً"
                              }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            !detailsModal.data.wallet_lock_sessions ||
                            !detailsModal.data.wallet_lock_sessions.length
                          "
                        >
                          <td colspan="6">
                            <div class="empty_tab_state py-5">
                              <div class="empty_state_icon bg_light_green">
                                <i class="fal fa-shield-check text-success"></i>
                              </div>
                              <h6
                                class="font-weight-bold mt-3 mb-1 text-success"
                              >
                                المحفظة مؤهلة وسليمة
                              </h6>
                              <p class="text-muted small mb-0">
                                لم يتم تسجيل أي فترات إغلاق للمحفظة أو استبعاد
                                أوقات عمل
                              </p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 4: Delay Records -->
              <v-tab-item>
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 55px">#</th>
                          <th>البداية المجدولة (الشفت)</th>
                          <th>البداية الفعلية للعمل</th>
                          <th>مدة التأخير المسجلة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(delay, dIdx) in detailsModal.data
                            .delay_records"
                          :key="dIdx"
                          class="session_row"
                        >
                          <td>
                            <span class="row_num_badge">{{ dIdx + 1 }}</span>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge">
                                <i
                                  class="fal fa-calendar-star ml-1 text-primary"
                                ></i>
                                {{
                                  formatTimeOnly(
                                    delay.scheduled_start_at ||
                                      delay.shift_start_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  delay.scheduled_start_at ||
                                    delay.shift_start_at
                                )
                              }}</span>
                            </div>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge text-danger">
                                <i
                                  class="fal fa-sign-in-alt ml-1 text-danger"
                                ></i>
                                {{
                                  formatTimeOnly(
                                    delay.actual_started_at ||
                                      delay.actual_open_at ||
                                      delay.started_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">{{
                                formatDateOnly(
                                  delay.actual_started_at ||
                                    delay.actual_open_at ||
                                    delay.started_at
                                )
                              }}</span>
                            </div>
                          </td>
                          <td>
                            <span class="delay_pill">
                              <i class="fal fa-exclamation-triangle ml-1"></i>
                              {{ formatMinutesToHHMM(delay.delay_minutes) }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            !detailsModal.data.delay_records ||
                            !detailsModal.data.delay_records.length
                          "
                        >
                          <td colspan="4">
                            <div class="empty_tab_state py-5">
                              <div class="empty_state_icon bg_light_green">
                                <i class="fal fa-clock-check text-success"></i>
                              </div>
                              <h6
                                class="font-weight-bold mt-3 mb-1 text-success"
                              >
                                المندوب ملتزم بمواعيد البدء
                              </h6>
                              <p class="text-muted small mb-0">
                                لا توجد حالات تأخير مسجلة لهذا اليوم ✨
                              </p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 5: Active Order Intervals -->
              <v-tab-item>
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th style="width: 55px">#</th>
                          <th>رقم / معرف الطلب</th>
                          <th>المتجر / العميل</th>
                          <th>بداية الفترة</th>
                          <th>نهاية الفترة</th>
                          <th>المدة المستغرقة</th>
                          <th>الحالة</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(interval, oIdx) in detailsModal.data
                            .active_order_intervals"
                          :key="oIdx"
                          class="session_row"
                        >
                          <td>
                            <span class="row_num_badge">{{ oIdx + 1 }}</span>
                          </td>
                          <td>
                            <div
                              class="d-flex align-items-center justify-content-center flex-wrap gap-1"
                            >
                              <router-link
                                v-if="getIntervalOrderId(interval)"
                                :to="`/delivery_orders/show/${getIntervalOrderId(interval)}`"
                                target="_blank"
                                class="order_ref_badge order_ref_link"
                                title="عرض تفاصيل الطلب"
                              >
                                <i class="fal fa-box ml-1 text-teal"></i>
                                #{{ getIntervalOrderNumber(interval) }}
                                <i
                                  class="fal fa-external-link-alt mr-1"
                                  style="font-size: 10px"
                                ></i>
                              </router-link>
                              <span
                                v-else-if="
                                  interval.order_number ||
                                  interval.order?.order_number ||
                                  interval.order_id ||
                                  interval.order?.id
                                "
                                class="order_ref_badge"
                              >
                                <i class="fal fa-box ml-1 text-teal"></i>
                                #{{ getIntervalOrderNumber(interval) }}
                              </span>
                              <span
                                v-else-if="
                                  interval.order_ids &&
                                  interval.order_ids.length
                                "
                                class="d-flex flex-wrap gap-1 justify-content-center"
                              >
                                <router-link
                                  v-for="(oId, oIdIdx) in interval.order_ids"
                                  :key="oIdIdx"
                                  :to="`/delivery_orders/show/${oId}`"
                                  target="_blank"
                                  class="order_ref_badge order_ref_link"
                                >
                                  <i class="fal fa-box ml-1 text-teal"></i>
                                  #{{ oId }}
                                </router-link>
                              </span>
                              <span
                                v-else-if="interval.orders_count"
                                class="order_ref_badge"
                              >
                                <i class="fal fa-boxes ml-1 text-teal"></i>
                                {{ interval.orders_count }} طلب
                              </span>
                              <span
                                v-else-if="interval.id"
                                class="order_ref_badge"
                              >
                                <i class="fal fa-hashtag ml-1 text-muted"></i>
                                {{ interval.id }}
                              </span>
                              <span v-else class="text-muted">-</span>
                            </div>
                          </td>
                          <td>
                            <div
                              class="d-flex flex-column align-items-center justify-content-center gap-1"
                            >
                              <span
                                v-if="
                                  interval.store_name ||
                                  interval.restaurant_name ||
                                  interval.store?.name ||
                                  interval.restaurant?.name ||
                                  interval.order?.store?.name ||
                                  interval.order?.restaurant?.name
                                "
                                class="district_chip"
                              >
                                <i class="fal fa-store ml-1 text-primary"></i>
                                {{
                                  interval.store_name ||
                                  interval.restaurant_name ||
                                  interval.store?.name ||
                                  interval.restaurant?.name ||
                                  interval.order?.store?.name ||
                                  interval.order?.restaurant?.name
                                }}
                              </span>
                              <span
                                v-if="
                                  interval.client_name ||
                                  interval.client?.name ||
                                  interval.client?.full_name ||
                                  interval.order?.client?.name ||
                                  interval.order?.client?.full_name
                                "
                                class="text-muted small"
                                style="font-size: 11px"
                              >
                                <i class="fal fa-user ml-1 text-muted"></i>
                                {{
                                  interval.client_name ||
                                  interval.client?.name ||
                                  interval.client?.full_name ||
                                  interval.order?.client?.name ||
                                  interval.order?.client?.full_name
                                }}
                              </span>
                              <span
                                v-if="
                                  !interval.store_name &&
                                  !interval.restaurant_name &&
                                  !interval.store?.name &&
                                  !interval.restaurant?.name &&
                                  !interval.order?.store?.name &&
                                  !interval.order?.restaurant?.name &&
                                  !interval.client_name &&
                                  !interval.client?.name &&
                                  !interval.client?.full_name &&
                                  !interval.order?.client?.name &&
                                  !interval.order?.client?.full_name
                                "
                                class="text-muted small"
                              >
                                {{
                                  interval.notes ||
                                  interval.title ||
                                  interval.source ||
                                  "-"
                                }}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div class="dt_cell">
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-primary"></i>
                                {{
                                  formatTimeOnly(
                                    interval.started_at ||
                                      interval.start_at ||
                                      interval.from ||
                                      interval.accepted_at ||
                                      interval.created_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">
                                {{
                                  formatDateOnly(
                                    interval.started_at ||
                                      interval.start_at ||
                                      interval.from ||
                                      interval.accepted_at ||
                                      interval.created_at
                                  )
                                }}
                              </span>
                            </div>
                          </td>
                          <td>
                            <div
                              class="dt_cell"
                              v-if="
                                interval.ended_at ||
                                interval.end_at ||
                                interval.to ||
                                interval.completed_at ||
                                interval.delivered_at ||
                                interval.closed_at
                              "
                            >
                              <span class="dt_time_badge">
                                <i class="fal fa-clock ml-1 text-secondary"></i>
                                {{
                                  formatTimeOnly(
                                    interval.ended_at ||
                                      interval.end_at ||
                                      interval.to ||
                                      interval.completed_at ||
                                      interval.delivered_at ||
                                      interval.closed_at
                                  )
                                }}
                              </span>
                              <span class="dt_date_sub">
                                {{
                                  formatDateOnly(
                                    interval.ended_at ||
                                      interval.end_at ||
                                      interval.to ||
                                      interval.completed_at ||
                                      interval.delivered_at ||
                                      interval.closed_at
                                  )
                                }}
                              </span>
                            </div>
                            <span v-else class="live_active_chip">
                              <span class="pulse_dot"></span>
                              قيد التنفيذ
                            </span>
                          </td>
                          <td>
                            <span
                              class="duration_pill"
                              :class="
                                isIntervalActive(interval)
                                  ? 'duration_active'
                                  : 'duration_closed'
                              "
                            >
                              <i class="fal fa-hourglass-half ml-1"></i>
                              {{
                                interval.duration_minutes !== undefined &&
                                interval.duration_minutes !== null
                                  ? formatMinutesToHHMM(
                                      interval.duration_minutes
                                    )
                                  : interval.duration &&
                                    !isNaN(Number(interval.duration))
                                  ? formatMinutesToHHMM(interval.duration)
                                  : calculateDuration(
                                      interval.started_at ||
                                        interval.start_at ||
                                        interval.from ||
                                        interval.accepted_at ||
                                        interval.created_at,
                                      interval.ended_at ||
                                        interval.end_at ||
                                        interval.to ||
                                        interval.completed_at ||
                                        interval.delivered_at ||
                                        interval.closed_at
                                    )
                              }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="status_pill"
                              :class="getOrderIntervalStatusClass(interval)"
                            >
                              <span
                                class="status_dot"
                                :class="
                                  isIntervalActive(interval)
                                    ? 'dot_pulse'
                                    : 'dot_gray'
                                "
                              ></span>
                              {{ getOrderIntervalStatusName(interval) }}
                            </span>
                          </td>
                        </tr>
                        <tr
                          v-if="
                            !detailsModal.data.active_order_intervals ||
                            !detailsModal.data.active_order_intervals.length
                          "
                        >
                          <td colspan="7">
                            <div class="empty_tab_state py-5">
                              <div class="empty_state_icon bg_light_teal">
                                <i class="fal fa-box-open text-teal"></i>
                              </div>
                              <h6 class="font-weight-bold mt-3 mb-1">
                                لا توجد فترات طلبات مسجلة
                              </h6>
                              <p class="text-muted small mb-0">
                                لم يتم رصد أي فترات تنفيذ طلبات نشطة لهذا اليوم
                              </p>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 6: Multi-Day Reports -->
              <v-tab-item
                v-if="
                  detailsModal.data.reports &&
                  detailsModal.data.reports.length > 1
                "
              >
                <div class="creative_table_card shadow-sm">
                  <div class="table-responsive">
                    <table
                      class="table creative_session_table text-center align-middle mb-0"
                    >
                      <thead>
                        <tr>
                          <th>التاريخ</th>
                          <th>الشفت المجدول</th>
                          <th>العمل الفعلي</th>
                          <th>العمل المعتمد</th>
                          <th>داخل / خارج الشفت</th>
                          <th>داخل / خارج الزون</th>
                          <th>التأخير</th>
                          <th>المحفظة</th>
                          <th>الطلبات</th>
                          <th>المسندة إداريًا</th>
                          <th>منتهية المهلة</th>
                          <th>أخذها سائق آخر</th>
                          <th>مغلقة</th>
                          <th>معلّقة</th>
                          <th>نسبة القبول</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(rep, rIdx) in detailsModal.data.reports"
                          :key="rIdx"
                          class="session_row"
                        >
                          <td>
                            <span
                              class="badge bg-light text-dark border font-weight-bold px-2 py-1"
                            >
                              <i
                                class="fal fa-calendar-day text-primary ml-1"
                              ></i>
                              {{ rep.report_date }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="text-dark font-weight-bold"
                              style="font-size: 12px"
                            >
                              {{
                                formatTimeOnly(
                                  rep.shift_start_at || rep.scheduled_start_at
                                )
                              }}
                              -
                              {{
                                formatTimeOnly(
                                  rep.shift_end_at || rep.scheduled_end_at
                                )
                              }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="text-success font-weight-bold"
                              style="font-size: 12px"
                            >
                              {{ formatTimeOnly(rep.actual_open_at) }} -
                              {{
                                rep.actual_close_at
                                  ? formatTimeOnly(rep.actual_close_at)
                                  : "مستمر"
                              }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="badge bg-primary text-white font-weight-bold px-2 py-1"
                            >
                              {{
                                formatMinutesToHHMM(rep.approved_work_minutes)
                              }}
                            </span>
                          </td>
                          <td>
                            <span class="text-primary font-weight-bold">{{
                              formatMinutesToHHMM(
                                rep.inside_shift_work_minutes ??
                                  rep.inside_working_time_minutes
                              )
                            }}</span>
                            /
                            <span class="text-purple font-weight-bold">{{
                              formatMinutesToHHMM(
                                rep.outside_shift_work_minutes ??
                                  rep.outside_working_time_minutes
                              )
                            }}</span>
                          </td>
                          <td>
                            <span class="text-success font-weight-bold">{{
                              formatMinutesToHHMM(rep.inside_zone_minutes)
                            }}</span>
                            /
                            <span class="text-amber font-weight-bold">{{
                              formatMinutesToHHMM(rep.outside_zone_minutes)
                            }}</span>
                          </td>
                          <td>
                            <span
                              :class="
                                rep.delay_minutes > 0
                                  ? 'text-danger font-weight-bold'
                                  : 'text-muted'
                              "
                            >
                              {{ formatMinutesToHHMM(rep.delay_minutes) }}
                            </span>
                          </td>
                          <td>
                            <span
                              :class="
                                rep.wallet_locked_excluded_minutes > 0
                                  ? 'text-danger font-weight-bold'
                                  : 'text-muted'
                              "
                            >
                              {{
                                formatMinutesToHHMM(
                                  rep.wallet_locked_excluded_minutes
                                )
                              }}
                            </span>
                          </td>
                          <td>
                            <span class="font-weight-bold"
                              >{{ rep.accepted_orders_count || 0 }} /
                              {{ rep.offered_orders_count || 0 }}</span
                            >
                            <span
                              v-if="rep.orders_count"
                              class="d-block text-muted"
                              style="font-size: 11px"
                            >
                              (منفذ: {{ rep.orders_count }})
                            </span>
                          </td>
                          <td>
                            <span
                              class="metric_chip"
                              :class="
                                rep.assigned_orders_count > 0
                                  ? 'chip_purple'
                                  : 'chip_gray'
                              "
                              :title="
                                'الطلبات المسندة إداريًا: ' +
                                (rep.assigned_orders_count || 0)
                              "
                            >
                              {{ rep.assigned_orders_count || 0 }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="metric_chip"
                              :class="
                                rep.expired_orders_count > 0
                                  ? 'chip_red'
                                  : 'chip_gray'
                              "
                              :title="
                                'الترشيحات المنتهية المهلة: ' +
                                (rep.expired_orders_count || 0)
                              "
                            >
                              {{ rep.expired_orders_count || 0 }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="metric_chip"
                              :class="
                                rep.taken_by_other_driver_orders_count > 0
                                  ? 'chip_amber'
                                  : 'chip_gray'
                              "
                              :title="
                                'الترشيحات التي أخذها سائق آخر: ' +
                                (rep.taken_by_other_driver_orders_count || 0)
                              "
                            >
                              {{ rep.taken_by_other_driver_orders_count || 0 }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="metric_chip"
                              :class="
                                rep.closed_orders_count > 0
                                  ? 'chip_orange'
                                  : 'chip_gray'
                              "
                              :title="
                                'الترشيحات المغلقة بسبب الإلغاء أو إعادة البحث: ' +
                                (rep.closed_orders_count || 0)
                              "
                            >
                              {{ rep.closed_orders_count || 0 }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="metric_chip"
                              :class="
                                rep.pending_orders_count > 0
                                  ? 'chip_blue'
                                  : 'chip_gray'
                              "
                              :title="
                                'الترشيحات المنشأة في يوم التقرير وما زالت معلّقة: ' +
                                (rep.pending_orders_count || 0)
                              "
                            >
                              {{ rep.pending_orders_count || 0 }}
                            </span>
                          </td>
                          <td>
                            <span
                              class="badge px-2 py-1 font-weight-bold"
                              :class="
                                getAcceptanceBadgeClass(rep.acceptance_rate)
                              "
                            >
                              {{ formatPercentage(rep.acceptance_rate) }}%
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </v-tab-item>

              <!-- Tab 6: Working Times -->
              <v-tab-item>
                <div class="working_times_grid row g-3">
                  <div
                    v-for="(wDay, dayIdx) in detailsModal.data.working_times ||
                    []"
                    :key="dayIdx"
                    class="col-md-4 col-sm-6"
                  >
                    <div
                      class="work_day_card p-3 rounded-xl border text-center"
                      :class="
                        wDay.enabled ? 'card_active_day' : 'card_holiday_day'
                      "
                    >
                      <div
                        class="d-flex align-items-center justify-content-between mb-2"
                      >
                        <h6 class="font-weight-bold mb-0 text-dark">
                          <i
                            class="fal fa-calendar-day ml-1"
                            :class="
                              wDay.enabled ? 'text-primary' : 'text-muted'
                            "
                          ></i>
                          {{ wDay.day_name || wDay.day }}
                        </h6>
                        <span
                          class="badge"
                          :class="
                            wDay.enabled
                              ? 'bg-success text-white'
                              : 'bg-secondary text-white'
                          "
                          style="font-size: 11px"
                        >
                          {{ wDay.enabled ? "مفعل" : "إجازة" }}
                        </span>
                      </div>
                      <div v-if="wDay.enabled" class="mt-2">
                        <div
                          v-if="wDay.times && wDay.times.length"
                          class="d-flex flex-wrap gap-1 justify-content-center"
                        >
                          <span
                            v-for="(t, tIdx) in wDay.times"
                            :key="tIdx"
                            class="work_time_badge"
                          >
                            <i class="fal fa-clock ml-1"></i>
                            {{ (t.start_time || t.from || "").slice(0, 5) }} -
                            {{ (t.end_time || t.to || "").slice(0, 5) }}
                          </span>
                        </div>
                        <div v-else-if="wDay.from">
                          <span class="work_time_badge">
                            <i class="fal fa-clock ml-1"></i>
                            {{ wDay.from.slice(0, 5) }} -
                            {{ wDay.to.slice(0, 5) }}
                          </span>
                        </div>
                      </div>
                      <div v-else class="text-muted small mt-2">
                        يوم عطلة أسبوعية للمندوب
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="
                      !detailsModal.data.working_times ||
                      !detailsModal.data.working_times.length
                    "
                    class="col-12"
                  >
                    <div class="empty_tab_state py-5">
                      <div class="empty_state_icon bg_light_purple">
                        <i class="fal fa-calendar-times text-primary"></i>
                      </div>
                      <h6 class="font-weight-bold mt-3 mb-1">
                        لا توجد أوقات عمل محددة
                      </h6>
                      <p class="text-muted small mb-0">
                        لم يتم إعداد جدول ساعات العمل الثابتة لهذا المندوب
                      </p>
                    </div>
                  </div>
                </div>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </v-card-text>

        <v-divider class="my-0"></v-divider>
        <!-- Creative Modal Footer -->
        <div
          class="modal_custom_footer p-3 px-4 d-flex justify-content-between align-items-center flex-wrap gap-2 bg-white"
        >
          <div class="d-flex align-items-center gap-2 text-muted small">
            <i class="fal fa-info-circle text-primary"></i>
            <span>البيانات محدثة ومستخرجة مباشرة من حركات نظام العمليات</span>
          </div>
          <div class="d-flex align-items-center gap-2">
            <button
              class="btn btn-outline-secondary btn-sm px-4 rounded-pill font-weight-bold"
              @click="detailsModal.open = false"
            >
              إغلاق
            </button>
            <button
              class="btn btn-gradient-primary btn-sm px-4 rounded-pill font-weight-bold d-flex align-items-center gap-2"
              @click="rebuildDriverReport(detailsModal.driver)"
              :disabled="rebuilding || detailsModal.loading"
            >
              <i class="fal fa-sync-alt" :class="{ 'fa-spin': rebuilding }"></i>
              <span>إعادة بناء تقرير هذا المندوب</span>
            </button>
          </div>
        </div>
      </v-card>
    </v-dialog>
    <!-- End Details Modal Dialog -->
  </div>
</template>

<script>
export default {
  name: "DriverOperationsReport",

  props: {
    showBreadcrumb: {
      type: Boolean,
      default: false,
    },
    initialFrom: {
      type: String,
      default: () => new Date().toISOString().slice(0, 10),
    },
    initialTo: {
      type: String,
      default: () => new Date().toISOString().slice(0, 10),
    },
    driverId: {
      type: [Number, String],
      default: null,
    },
    driver: {
      type: Object,
      default: null,
    },
  },

  data() {
    const today = new Date().toISOString().slice(0, 10);
    return {
      loading: false,
      rebuilding: false,
      rebuildingDriverId: null,
      breadcrumbItems: [
        { text: this.$t("breadcrumb.mainPage"), disabled: false, href: "/" },
        { text: "تقرير نظام أوقات عمل المندوبين", disabled: true, href: "" },
      ],

      // Filters
      filters: {
        from: this.initialFrom || today,
        to: this.initialTo || today,
        driver_id: null,
        selected_driver: null,
        has_delay: null,
        has_delay_obj: null,
        has_wallet_lock: null,
        has_wallet_lock_obj: null,
      },

      delayOptions: [
        { name: "الكل", value: null },
        { name: "لديهم تأخير فقط", value: 1 },
        { name: "بدون تأخير", value: 0 },
      ],

      walletLockOptions: [
        { name: "الكل", value: null },
        { name: "محفظة مقفلة فقط", value: 1 },
        { name: "محفظة مؤهلة", value: 0 },
      ],

      driversList: [],

      // Summary
      summary: {
        scheduled_drivers_count: 0,
        delayed_drivers_count: 0,
        total_approved_work_minutes: 0,
        average_acceptance_rate: 0,
        total_wallet_locked_excluded_minutes: 0,
        total_outside_zone_minutes: 0,
        total_assigned_orders_count: 0,
        total_expired_orders_count: 0,
        total_taken_by_other_driver_orders_count: 0,
        total_closed_orders_count: 0,
        total_pending_orders_count: 0,
        order_delay_minutes_total: 0,
        delayed_orders_count_total: 0,
        order_delay_percentage_avg: 0,
      },

      // Table & Pagination
      rows: [],
      page: 1,
      per_page: 15,
      perPageOptions: [10, 15, 20, 25, 50, 100],
      totalRows: 0,
      lastPage: 1,
      fromRecord: 0,
      toRecord: 0,
      paginations: {
        current_page: 1,
        last_page: 1,
        items_per_page: 15,
      },

      // Details Modal
      detailsModal: {
        open: false,
        loading: false,
        activeTab: 0,
        driver: null,
        date: null,
        data: {},
      },

      // Table Headers
      headers: [
        { text: "#", value: "index", align: "center", sortable: false },
        { text: "المندوب", value: "driver", align: "right" },
        { text: "تاريخ العمل", value: "report_date", align: "center" },
        { text: "وقت العمل المجدول", value: "shift_time", align: "center" },
        { text: "العمل الفعلي", value: "actual_time", align: "center" },
        {
          text: "داخل وقت العمل",
          value: "inside_shift_work_minutes",
          align: "center",
        },
        {
          text: "خارج وقت العمل",
          value: "outside_shift_work_minutes",
          align: "center",
        },
        { text: "داخل الزون", value: "inside_zone_minutes", align: "center" },
        { text: "خارج الزون", value: "outside_zone_minutes", align: "center" },
        { text: "التأخير", value: "delay_minutes", align: "center" },
        {
          text: "استبعاد المحفظة",
          value: "wallet_locked_excluded_minutes",
          align: "center",
        },
        {
          text: "العمل المعتمد",
          value: "approved_work_minutes",
          align: "center",
        },
        {
          text: "معدل الالتزام بالمواعيد",
          value: "on_time_adherence_rate",
          align: "center",
        },
        {
          text: "تأخير الطلبات",
          value: "order_delay",
          align: "center",
        },
        {
          text: "الطلبات ونسبة القبول",
          value: "orders_and_acceptance",
          align: "center",
        },
        {
          text: "الإجراءات",
          value: "actions",
          align: "center",
          sortable: false,
          width: "210px",
        },
      ],
    };
  },

  watch: {
    "filters.from"(newVal) {
      const queryVal =
        this.$route.query.from || this.$route.query.start_date || "";
      if (newVal && newVal !== queryVal) {
        this.onDateFilterChange();
      }
    },
    "filters.to"(newVal) {
      const queryVal =
        this.$route.query.to || this.$route.query.end_date || "";
      if (newVal && newVal !== queryVal) {
        this.onDateFilterChange();
      }
    },
    [`$route`]() {
      let shouldFetch = false;
      const today = new Date().toISOString().slice(0, 10);

      // Watch from date
      const fromInQuery =
        this.$route.query.from || this.$route.query.start_date || today;
      if (this.filters.from !== fromInQuery) {
        this.filters.from = fromInQuery;
        shouldFetch = true;
      }

      // Watch to date
      const toInQuery =
        this.$route.query.to || this.$route.query.end_date || today;
      if (this.filters.to !== toInQuery) {
        this.filters.to = toInQuery;
        shouldFetch = true;
      }

      // Watch page
      const pageInQuery = this.$route.query.page
        ? +this.$route.query.page
        : 1;
      if (this.page !== pageInQuery) {
        this.page = pageInQuery;
        this.paginations.current_page = pageInQuery;
        shouldFetch = true;
      }

      if (shouldFetch) {
        this.fetchReportData();
      }
    },
  },

  computed: {
    tableItems() {
      return (Array.isArray(this.rows) ? this.rows : []).map((row, idx) => ({
        ...row,
        _rowKey:
          row.id ||
          `${row.driver_id || row.driver?.id || "d"}_${row.report_date || row.date || idx}_${idx}`,
      }));
    },
    totalPages() {
      if (this.lastPage) return this.lastPage;
      if (this.paginations?.last_page) return this.paginations.last_page;
      return Math.ceil(this.totalRows / this.per_page) || 1;
    },
    modalActiveReport() {
      if (!this.detailsModal.data) return null;
      if (this.detailsModal.data.report) return this.detailsModal.data.report;
      if (
        Array.isArray(this.detailsModal.data.reports) &&
        this.detailsModal.data.reports.length
      ) {
        return (
          this.detailsModal.data.reports.find(
            (r) => r.report_date === this.detailsModal.date
          ) || this.detailsModal.data.reports[0]
        );
      }
      return this.detailsModal.driverReport || null;
    },
  },

  methods: {
    async fetchDrivers() {
      try {
        const res = await this.$axios.get("driver_not_paginated");
        this.driversList = (res.data.data || []).map((d) => ({
          id: d.id,
          full_name: d.full_name || d.name || `#${d.id}`,
          phone: d.phone || "",
          image: d.image || d.avatar || "",
        }));
      } catch {
        // Ignore fallback
      }
    },

    setRows() {
      return this.fetchReportData();
    },

    fetchData(e) {
      const targetPage = +e;
      this.page = targetPage;
      this.paginations.current_page = targetPage;

      const newQuery = {
        ...this.$route.query,
        page: targetPage,
      };
      if (this.filters.from) newQuery.from = this.filters.from;
      if (this.filters.to) newQuery.to = this.filters.to;

      this.$router
        .push({ query: newQuery })
        .catch(() => {});

      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });

      this.fetchReportData();
    },

    onPageChange(newPage) {
      this.fetchData(newPage);
    },

    onDriverSelect(driver) {
      this.filters.driver_id = driver ? driver.id : null;
      this.page = 1;
      this.paginations.current_page = 1;
      this.fetchReportData();
    },

    onDateFilterChange() {
      if (this._dateFilterTimeout) {
        clearTimeout(this._dateFilterTimeout);
      }
      this._dateFilterTimeout = setTimeout(() => {
        this.onFilterChange();
      }, 50);
    },

    onFilterChange() {
      this.page = 1;
      this.paginations.current_page = 1;

      const newQuery = {
        ...this.$route.query,
        page: 1,
      };

      if (this.filters.from) {
        newQuery.from = this.filters.from;
      } else {
        delete newQuery.from;
      }

      if (this.filters.to) {
        newQuery.to = this.filters.to;
      } else {
        delete newQuery.to;
      }

      delete newQuery.start_date;
      delete newQuery.end_date;

      const isSameQuery =
        this.$route.query.from === newQuery.from &&
        this.$route.query.to === newQuery.to &&
        (+this.$route.query.page || 1) === 1;

      if (!isSameQuery) {
        this.$router
          .push({ query: newQuery })
          .catch(() => {});
      }

      this.fetchReportData();
    },

    onPerPageChange() {
      this.page = 1;
      this.paginations.current_page = 1;
      this.fetchReportData();
    },

    selectPerPage(opt) {
      if (this.per_page === opt) return;
      this.per_page = opt;
      this.page = 1;
      this.paginations.current_page = 1;
      this.fetchReportData();
    },

    resetFilters() {
      const today = new Date().toISOString().slice(0, 10);
      this.filters = {
        from: today,
        to: today,
        driver_id: null,
        selected_driver: null,
        has_delay: null,
        has_delay_obj: null,
        has_wallet_lock: null,
        has_wallet_lock_obj: null,
      };
      this.page = 1;
      this.paginations.current_page = 1;

      const newQuery = {
        ...this.$route.query,
        page: 1,
        from: today,
        to: today,
      };

      delete newQuery.start_date;
      delete newQuery.end_date;

      this.$router
        .push({ query: newQuery })
        .catch(() => {});

      this.fetchReportData();
    },

    async fetchReportData() {
      this.loading = true;
      const params = {
        from: this.filters.from,
        to: this.filters.to,
        page: this.page,
        per_page: this.per_page,
      };
      if (this.filters.driver_id) params.driver_id = this.filters.driver_id;
      if (
        this.filters.has_delay !== null &&
        this.filters.has_delay !== undefined
      ) {
        params.has_delay = this.filters.has_delay;
      }
      if (
        this.filters.has_wallet_lock !== null &&
        this.filters.has_wallet_lock !== undefined
      ) {
        params.has_wallet_lock = this.filters.has_wallet_lock;
      }

      try {
        const res = await this.$axios.get("driver-operations-report", {
          params,
        });
        const responseData = res.data.data || res.data || {};

        // Extract meta safely from API response (sibling on res.data, or inside data)
        const meta =
          res.data?.meta ||
          responseData?.meta ||
          (responseData?.reports && responseData.reports.meta) ||
          {};

        // Parse table reports safely
        let extractedRows = [];
        if (Array.isArray(responseData.reports)) {
          extractedRows = responseData.reports;
        } else if (
          responseData.reports &&
          Array.isArray(responseData.reports.data)
        ) {
          extractedRows = responseData.reports.data;
        } else if (
          responseData.reports &&
          typeof responseData.reports === "object"
        ) {
          extractedRows = Object.values(responseData.reports);
        } else if (Array.isArray(responseData.items)) {
          extractedRows = responseData.items;
        } else if (
          responseData.items &&
          Array.isArray(responseData.items.data)
        ) {
          extractedRows = responseData.items.data;
        } else if (Array.isArray(responseData.data)) {
          extractedRows = responseData.data;
        } else if (Array.isArray(responseData)) {
          extractedRows = responseData;
        }

        this.rows = Array.isArray(extractedRows) ? extractedRows : [];

        // Parse total rows
        if (meta.total !== undefined && meta.total !== null) {
          this.totalRows = Number(meta.total);
        } else if (responseData.total !== undefined && responseData.total !== null) {
          this.totalRows = Number(responseData.total);
        } else if (responseData.reports && responseData.reports.total !== undefined) {
          this.totalRows = Number(responseData.reports.total);
        } else {
          this.totalRows = this.rows.length;
        }

        // Parse last page / total pages
        if (meta.last_page !== undefined && meta.last_page !== null) {
          this.lastPage = Number(meta.last_page);
          this.paginations.last_page = Number(meta.last_page);
        } else {
          this.lastPage = Math.ceil(this.totalRows / this.per_page) || 1;
          this.paginations.last_page = this.lastPage;
        }

        if (meta.per_page !== undefined && meta.per_page !== null) {
          this.paginations.items_per_page = Number(meta.per_page);
        }

        // Current page
        if (meta.current_page !== undefined && meta.current_page !== null) {
          this.page = Number(meta.current_page);
          this.paginations.current_page = Number(meta.current_page);
        }

        // From and to records range
        if (meta.from !== undefined && meta.from !== null) {
          this.fromRecord = Number(meta.from);
        } else {
          this.fromRecord = this.rows.length ? (this.page - 1) * this.per_page + 1 : 0;
        }

        if (meta.to !== undefined && meta.to !== null) {
          this.toRecord = Number(meta.to);
        } else {
          this.toRecord = this.rows.length ? (this.page - 1) * this.per_page + this.rows.length : 0;
        }

        // Parse summary cards with fallback calculations
        const rawSummary =
          responseData.summary || responseData.statistics || {};

        const computedScheduled = this.rows.filter(
          (r) => r.shift_start_at || r.scheduled_start_at
        ).length;
        const computedDelayed = this.rows.filter(
          (r) => Number(r.delay_minutes) > 0
        ).length;
        const computedApproved = this.rows.reduce(
          (acc, r) => acc + (Number(r.approved_work_minutes) || 0),
          0
        );
        const computedWalletLocked = this.rows.reduce(
          (acc, r) => acc + (Number(r.wallet_locked_excluded_minutes) || 0),
          0
        );
        const computedOutsideZone = this.rows.reduce(
          (acc, r) => acc + (Number(r.outside_zone_minutes) || 0),
          0
        );
        const computedRates = this.rows
          .map((r) => Number(r.acceptance_rate))
          .filter((v) => !isNaN(v) && v > 0);
        const computedAvgRate = computedRates.length
          ? (
              computedRates.reduce((a, b) => a + b, 0) / computedRates.length
            ).toFixed(1)
          : 0;
        const computedOrderDelayMinutes = this.rows.reduce(
          (acc, r) => acc + (Number(r.order_delay_minutes) || 0),
          0
        );
        const computedDelayedOrdersCount = this.rows.reduce(
          (acc, r) => acc + (Number(r.delayed_orders_count) || 0),
          0
        );
        const computedOrderDelayRates = this.rows
          .map((r) => Number(r.order_delay_percentage))
          .filter((v) => !isNaN(v) && v > 0);
        const computedOrderDelayAvgRate = computedOrderDelayRates.length
          ? (
              computedOrderDelayRates.reduce((a, b) => a + b, 0) /
              computedOrderDelayRates.length
            ).toFixed(1)
          : 0;

        this.summary = {
          scheduled_drivers_count:
            rawSummary.scheduled_drivers_count ||
            rawSummary.drivers_with_working_times ||
            computedScheduled ||
            0,
          delayed_drivers_count:
            rawSummary.delayed_drivers_count || computedDelayed || 0,
          total_approved_work_minutes:
            rawSummary.total_approved_work_minutes ||
            rawSummary.approved_work_minutes ||
            computedApproved ||
            0,
          average_acceptance_rate:
            rawSummary.average_acceptance_rate ||
            rawSummary.acceptance_rate ||
            computedAvgRate ||
            0,
          total_wallet_locked_excluded_minutes:
            rawSummary.total_wallet_locked_excluded_minutes ||
            rawSummary.wallet_locked_excluded_minutes ||
            computedWalletLocked ||
            0,
          total_outside_zone_minutes:
            rawSummary.total_outside_zone_minutes ||
            rawSummary.outside_zone_minutes ||
            computedOutsideZone ||
            0,
          total_assigned_orders_count:
            rawSummary.total_assigned_orders_count ||
            rawSummary.assigned_orders_count ||
            this.rows.reduce(
              (acc, r) => acc + (Number(r.assigned_orders_count) || 0),
              0
            ),
          total_expired_orders_count:
            rawSummary.total_expired_orders_count ||
            rawSummary.expired_orders_count ||
            this.rows.reduce(
              (acc, r) => acc + (Number(r.expired_orders_count) || 0),
              0
            ),
          total_taken_by_other_driver_orders_count:
            rawSummary.total_taken_by_other_driver_orders_count ||
            rawSummary.taken_by_other_driver_orders_count ||
            this.rows.reduce(
              (acc, r) =>
                acc + (Number(r.taken_by_other_driver_orders_count) || 0),
              0
            ),
          total_closed_orders_count:
            rawSummary.total_closed_orders_count ||
            rawSummary.closed_orders_count ||
            this.rows.reduce(
              (acc, r) => acc + (Number(r.closed_orders_count) || 0),
              0
            ),
          total_pending_orders_count:
            rawSummary.total_pending_orders_count ||
            rawSummary.pending_orders_count ||
            this.rows.reduce(
              (acc, r) => acc + (Number(r.pending_orders_count) || 0),
              0
            ),
          order_delay_minutes_total:
            rawSummary.order_delay_minutes_total ||
            computedOrderDelayMinutes ||
            0,
          delayed_orders_count_total:
            rawSummary.delayed_orders_count_total ||
            computedDelayedOrdersCount ||
            0,
          order_delay_percentage_avg:
            rawSummary.order_delay_percentage_avg ||
            computedOrderDelayAvgRate ||
            0,
        };
      } catch (err) {
        console.error("fetchReportData error:", err);
        this.rows = [];
      } finally {
        this.loading = false;
      }
    },

    async openDetailsModal(item) {
      const driverId = this.getDriverId(item);
      this.detailsModal.driver = item.driver || item;
      this.detailsModal.driverReport = item;
      this.detailsModal.date =
        item.report_date || item.date || this.filters.from;
      this.detailsModal.open = true;
      this.detailsModal.loading = true;
      this.detailsModal.activeTab = 0;
      this.detailsModal.data = {};

      const params = {
        date: this.detailsModal.date,
        from: this.filters.from,
        to: this.filters.to,
        report_id: item.id
      };

      try {
        const res = await this.$axios.get(
          `driver-operations-report/${driverId}`,
          { params }
        );
        const modalRaw = res.data.data || res.data || {};

        let modalReports = [];
        if (Array.isArray(modalRaw.reports)) {
          modalReports = modalRaw.reports;
        } else if (modalRaw.reports && Array.isArray(modalRaw.reports.data)) {
          modalReports = modalRaw.reports.data;
        } else if (modalRaw.report) {
          modalReports = [modalRaw.report];
        } else if (modalRaw.reports && typeof modalRaw.reports === "object") {
          modalReports = Object.values(modalRaw.reports);
        }

        const activeReport =
          modalReports.find((r) => r.report_date === this.detailsModal.date) ||
          modalReports[0] ||
          modalRaw.report ||
          item;

        this.detailsModal.data = {
          reports: modalReports,
          report: activeReport,
          activity_sessions: Array.isArray(modalRaw.activity_sessions)
            ? modalRaw.activity_sessions
            : modalRaw.activity_sessions &&
              typeof modalRaw.activity_sessions === "object"
            ? Object.values(modalRaw.activity_sessions)
            : [],
          zone_sessions: Array.isArray(modalRaw.zone_sessions)
            ? modalRaw.zone_sessions
            : modalRaw.zone_sessions &&
              typeof modalRaw.zone_sessions === "object"
            ? Object.values(modalRaw.zone_sessions)
            : [],
          wallet_lock_sessions: Array.isArray(modalRaw.wallet_lock_sessions)
            ? modalRaw.wallet_lock_sessions
            : modalRaw.wallet_lock_sessions &&
              typeof modalRaw.wallet_lock_sessions === "object"
            ? Object.values(modalRaw.wallet_lock_sessions)
            : [],
          delay_records: Array.isArray(modalRaw.delay_records)
            ? modalRaw.delay_records
            : modalRaw.delay_records &&
              typeof modalRaw.delay_records === "object"
            ? Object.values(modalRaw.delay_records)
            : [],
          active_order_intervals: Array.isArray(modalRaw.active_order_intervals)
            ? modalRaw.active_order_intervals
            : modalRaw.active_order_intervals &&
              typeof modalRaw.active_order_intervals === "object"
            ? Object.values(modalRaw.active_order_intervals)
            : Array.isArray(activeReport?.active_order_intervals)
            ? activeReport.active_order_intervals
            : activeReport?.active_order_intervals &&
              typeof activeReport.active_order_intervals === "object"
            ? Object.values(activeReport.active_order_intervals)
            : Array.isArray(modalRaw.report?.active_order_intervals)
            ? modalRaw.report.active_order_intervals
            : Array.isArray(modalRaw.order_intervals)
            ? modalRaw.order_intervals
            : [],
          working_times: Array.isArray(modalRaw.working_times)
            ? modalRaw.working_times
            : modalRaw.working_times &&
              typeof modalRaw.working_times === "object"
            ? Object.values(modalRaw.working_times)
            : [],
        };
      } catch (err) {
        console.error("openDetailsModal error:", err);
        this.detailsModal.data = {
          reports: [item],
          report: item,
          activity_sessions: Array.isArray(item.activity_sessions)
            ? item.activity_sessions
            : [],
          zone_sessions: Array.isArray(item.zone_sessions)
            ? item.zone_sessions
            : [],
          wallet_lock_sessions: Array.isArray(item.wallet_lock_sessions)
            ? item.wallet_lock_sessions
            : [],
          delay_records: Array.isArray(item.delay_records)
            ? item.delay_records
            : [],
          active_order_intervals: Array.isArray(item.active_order_intervals)
            ? item.active_order_intervals
            : item.report?.active_order_intervals &&
              Array.isArray(item.report.active_order_intervals)
            ? item.report.active_order_intervals
            : Array.isArray(item.order_intervals)
            ? item.order_intervals
            : [],
          working_times: Array.isArray(
            item.working_times || item.driver?.working_times
          )
            ? item.working_times || item.driver?.working_times
            : [],
        };
      } finally {
        this.detailsModal.loading = false;
      }
    },

    // Formatters & Helpers
    getDriverId(item) {
      if (!item) return "";
      return item.driver_id || item.driver?.id || item.id || "";
    },

    getDriverName(item) {
      if (!item) return "-";
      return (
        item.driver?.full_name ||
        item.driver?.name ||
        item.driver_name ||
        item.full_name ||
        item.name ||
        (item.driver_id || item.id ? `#${item.driver_id || item.id}` : "-")
      );
    },

    getDriverPhone(item) {
      if (!item) return "";
      return item.driver?.phone || item.driver_phone || item.phone || "";
    },

    getDriverImage(item) {
      if (!item) return "";
      return (
        item.driver?.image ||
        item.driver?.avatar ||
        item.image ||
        item.avatar ||
        ""
      );
    },

    formatTimeOnly(dateTimeStr) {
      if (!dateTimeStr) return "-";
      try {
        if (typeof dateTimeStr !== "string") {
          dateTimeStr = String(dateTimeStr);
        }
        if (dateTimeStr.includes("T") || dateTimeStr.includes(" ")) {
          const parts = dateTimeStr.split(/[ T]/);
          if (parts[1]) return parts[1].slice(0, 5);
        }
        return dateTimeStr.slice(0, 5);
      } catch (e) {
        return "-";
      }
    },

    formatDateOnly(dateTimeStr) {
      if (!dateTimeStr) return "";
      try {
        if (typeof dateTimeStr !== "string") {
          dateTimeStr = String(dateTimeStr);
        }
        const clean = dateTimeStr.split(/[ T]/)[0];
        return clean || "";
      } catch (e) {
        return "";
      }
    },

    getReadableSource(source) {
      if (!source) return "التطبيق";
      const s = String(source).toLowerCase();
      if (s === "availability" || s.includes("availab")) return "إتاحة (App)";
      if (s.includes("candidate")) return "نظام الإسناد";
      if (s.includes("manual") || s.includes("admin")) return "لوحة التحكم";
      if (s.includes("auto")) return "تلقائي";
      return source;
    },

    formatDateTime(dateTimeStr) {
      if (!dateTimeStr) return "-";
      try {
        if (typeof dateTimeStr !== "string") {
          dateTimeStr = String(dateTimeStr);
        }
        return dateTimeStr.replace("T", " ").slice(0, 16);
      } catch (e) {
        return "-";
      }
    },

    formatMinutesToHHMM(minutes, withUnit = true) {
      if (minutes === undefined || minutes === null || isNaN(Number(minutes))) {
        return withUnit ? "00:00 س" : "00:00";
      }
      const totalMins = Math.max(0, Math.round(Number(minutes) || 0));
      const h = Math.floor(totalMins / 60);
      const m = totalMins % 60;
      const hh = h < 10 ? `0${h}` : `${h}`;
      const mm = m < 10 ? `0${m}` : `${m}`;
      const timeStr = `${hh}:${mm}`;
      return withUnit ? `${timeStr} س` : timeStr;
    },

    formatMinutesToHours(minutes) {
      return this.formatMinutesToHHMM(minutes);
    },

    formatMinutesDetail(minutes) {
      return this.formatMinutesToHHMM(minutes);
    },

    formatPercentage(rate) {
      if (rate === undefined || rate === null || isNaN(Number(rate)))
        return "0";
      const num = Number(rate);
      return num.toFixed(1);
    },

    formatAdherenceRate(rate) {
      if (rate === undefined || rate === null || isNaN(Number(rate)))
        return "-";
      return `${Number(rate).toFixed(2)}%`;
    },

    getAcceptanceBadgeClass(rate) {
      const num = Number(rate) || 0;
      if (num >= 85) return "bg-success text-white";
      if (num >= 60) return "bg-primary text-white";
      if (num >= 40) return "bg-warning text-dark";
      return "bg-danger text-white";
    },

    calculateDuration(startedAt, endedAt) {
      if (!startedAt) return "-";
      try {
        const start = new Date(startedAt);
        if (isNaN(start.getTime())) return "-";
        const end = endedAt ? new Date(endedAt) : new Date();
        if (isNaN(end.getTime())) return "-";
        const diffMs = end - start;
        if (diffMs <= 0) return "00:00";
        const mins = Math.round(diffMs / (1000 * 60));
        return this.formatMinutesToHHMM(mins);
      } catch (e) {
        return "-";
      }
    },

    isIntervalActive(interval) {
      if (!interval) return false;
      if (interval.is_closed === false) return true;
      if (interval.is_closed === true) return false;
      if (
        interval.ended_at ||
        interval.end_at ||
        interval.to ||
        interval.delivered_at ||
        interval.completed_at ||
        interval.closed_at
      ) {
        return false;
      }
      return true;
    },

    getOrderIntervalStatusName(interval) {
      if (!interval) return "-";
      if (interval.status_trans) return interval.status_trans;
      if (interval.status_text) return interval.status_text;
      const rawStatus =
        interval.status || interval.order_status || interval.order?.status;
      if (rawStatus) {
        const s = String(rawStatus).toLowerCase();
        const map = {
          completed: "مكتمل",
          delivered: "تم التوصيل",
          admin_delivered: "تم التوصيل",
          accepted: "مقبول",
          admin_accept: "مقبول",
          in_progress: "قيد التنفيذ",
          pending: "معلق",
          shipping: "جاري التوصيل",
          admin_shipping: "جاري التوصيل",
          driver_accept: "قيد التوصيل",
          driver_in_the_way: "في الطريق للعميل",
          at_restaurant: "في المطعم",
          driver_pre_finished: "تم التسليم المبدئي",
          canceled: "ملغي",
          admin_cancel: "ملغي",
          client_cancel: "ملغي من العميل",
          driver_cancel: "ملغي من المندوب",
          driver_rejected: "مرفوض من المندوب",
          closed: "مغلقة",
          active: "نشطة",
        };
        if (map[s]) return map[s];
        return rawStatus;
      }
      if (interval.is_closed !== undefined && interval.is_closed !== null) {
        return interval.is_closed ? "مغلقة" : "نشطة";
      }
      if (
        interval.ended_at ||
        interval.end_at ||
        interval.to ||
        interval.delivered_at ||
        interval.completed_at
      ) {
        return "مكتملة";
      }
      return "نشطة";
    },

    getOrderIntervalStatusClass(interval) {
      if (!interval) return "pill_closed";
      const rawStatus = String(
        interval.status ||
          interval.order_status ||
          interval.order?.status ||
          ""
      ).toLowerCase();
      if (
        rawStatus.includes("cancel") ||
        rawStatus.includes("reject") ||
        rawStatus === "failed"
      ) {
        return "pill_danger";
      }
      if (
        rawStatus.includes("deliver") ||
        rawStatus.includes("complete") ||
        rawStatus.includes("finish") ||
        rawStatus === "closed"
      ) {
        return "pill_success";
      }
      if (
        rawStatus.includes("progress") ||
        rawStatus.includes("ship") ||
        rawStatus.includes("way") ||
        rawStatus === "active" ||
        rawStatus === "pending" ||
        interval.is_closed === false
      ) {
        return "pill_open";
      }
      if (interval.is_closed) return "pill_closed";
      if (
        interval.ended_at ||
        interval.end_at ||
        interval.to ||
        interval.delivered_at ||
        interval.completed_at
      ) {
        return "pill_success";
      }
      return "pill_open";
    },

    getIntervalOrderId(interval) {
      if (!interval) return null;
      return interval.order_id || interval.order?.id || null;
    },

    getIntervalOrderNumber(interval) {
      if (!interval) return "-";
      return (
        interval.order_number ||
        interval.order?.order_number ||
        interval.order_id ||
        interval.order?.id ||
        (interval.id ? `${interval.id}` : "-")
      );
    },

    exportToCSV() {
      if (!this.rows.length) return;
      const headers = [
        "اسم المندوب",
        "تاريخ العمل",
        "بداية الشفت المجدول",
        "نهاية الشفت المجدول",
        "بداية العمل الفعلي",
        "نهاية العمل الفعلي",
        "داخل الشفت (دقائق)",
        "خارج الشفت (دقائق)",
        "داخل الزون (دقائق)",
        "خارج الزون (دقائق)",
        "التأخير (دقائق)",
        "المستبعد بسبب المحفظة (دقائق)",
        "العمل المعتمد (دقائق)",
        "معدل الالتزام بالمواعيد",
        "إجمالي الطلبات",
        "الطلبات المعروضة",
        "الطلبات المقبولة",
        "طلبات لم تقبل",
        "طلبات مرفوضة يدوياً",
        "الطلبات المسندة إداريًا",
        "الترشيحات المنتهية المهلة",
        "الترشيحات التي أخذها سائق آخر",
        "الترشيحات المغلقة بسبب الإلغاء أو إعادة البحث",
        "الترشيحات المعلّقة",
        "نسبة القبول (%)",
      ];

      const csvRows = this.rows.map((r) => [
        `"${this.getDriverName(r)}"`,
        `"${r.report_date || r.date || ""}"`,
        `"${this.formatTimeOnly(r.shift_start_at || r.scheduled_start_at)}"`,
        `"${this.formatTimeOnly(r.shift_end_at || r.scheduled_end_at)}"`,
        `"${this.formatTimeOnly(r.actual_open_at)}"`,
        `"${this.formatTimeOnly(r.actual_close_at)}"`,
        `"${this.formatMinutesToHHMM(
          r.inside_shift_work_minutes !== undefined &&
            r.inside_shift_work_minutes !== null
            ? r.inside_shift_work_minutes
            : r.inside_working_time_minutes
        )}"`,
        `"${this.formatMinutesToHHMM(
          r.outside_shift_work_minutes !== undefined &&
            r.outside_shift_work_minutes !== null
            ? r.outside_shift_work_minutes
            : r.outside_working_time_minutes
        )}"`,
        `"${this.formatMinutesToHHMM(r.inside_zone_minutes)}"`,
        `"${this.formatMinutesToHHMM(r.outside_zone_minutes)}"`,
        `"${this.formatMinutesToHHMM(r.delay_minutes)}"`,
        `"${this.formatMinutesToHHMM(r.wallet_locked_excluded_minutes)}"`,
        `"${this.formatMinutesToHHMM(r.approved_work_minutes)}"`,
        `"${this.formatAdherenceRate(r.on_time_adherence_rate)}"`,
        r.orders_count || 0,
        r.offered_orders_count || 0,
        r.accepted_orders_count || 0,
        r.not_accepted_orders_count || 0,
        r.manual_rejected_orders_count || 0,
        r.assigned_orders_count || 0,
        r.expired_orders_count || 0,
        r.taken_by_other_driver_orders_count || 0,
        r.closed_orders_count || 0,
        r.pending_orders_count || 0,
        r.acceptance_rate || 0,
      ]);

      const csvContent =
        "\uFEFF" +
        [headers.join(","), ...csvRows.map((e) => e.join(","))].join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `تقرير_عمليات_المندوبين_${this.filters.from}_${this.filters.to}.csv`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    async rebuildDriverReport(target = null) {
      let resolvedDriverId = null;

      if (target) {
        if (typeof target === "object") {
          resolvedDriverId = this.getDriverId(target);
        } else {
          resolvedDriverId = target;
        }
      } else {
        // Resolve from active filters, props, or route params
        resolvedDriverId =
          this.filters.driver_id ||
          (this.filters.selected_driver && this.filters.selected_driver.id) ||
          this.driverId ||
          this.$route?.params?.driver_id ||
          this.$route?.params?.id;

        // If only 1 driver row in current view, use it
        if (!resolvedDriverId && this.rows.length === 1) {
          resolvedDriverId = this.getDriverId(this.rows[0]);
        }
      }

      if (!resolvedDriverId) {
        if (this.$iziToast) {
          this.$iziToast.warning({
            timeout: 4000,
            message: "يرجى تحديد المندوب من القائمة أولاً لإعادة بناء التقرير",
            position: "bottomRight",
          });
        }
        return;
      }

      this.rebuilding = true;
      this.rebuildingDriverId = resolvedDriverId;

      const dateFrom =
        (typeof target === "object" && (target?.report_date || target?.date)) ||
        this.filters.from;
      const dateTo =
        (typeof target === "object" && (target?.report_date || target?.date)) ||
        this.filters.to;

      const payload = {
        from: dateFrom,
        to: dateTo,
        date: dateFrom,
      };

      try {
        const res = await this.$axios({
          method: "POST",
          url: `driver-operations-report/${resolvedDriverId}/rebuild`,
          params: payload,
          data: payload,
        });

        const responseMsg =
          res.data?.message ||
          res.data?.msg ||
          "تمت إعادة بناء تقرير عمليات المندوب بنجاح";

        if (this.$iziToast) {
          this.$iziToast.success({
            timeout: 4000,
            message: responseMsg,
            position: "bottomRight",
          });
        }

        // Refresh table & summary
        await this.fetchReportData();

        // Refresh modal if currently open for this driver
        if (
          this.detailsModal.open &&
          this.getDriverId(this.detailsModal.driver) === resolvedDriverId
        ) {
          await this.openDetailsModal(this.detailsModal.driver);
        }
      } catch (err) {
        const responseMsg =
          err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "تعذر إعادة بناء التقرير، يرجى المحاولة لاحقاً";

        if (this.$iziToast) {
          this.$iziToast.error({
            timeout: 4000,
            message: responseMsg,
            position: "bottomRight",
          });
        }
      } finally {
        this.rebuilding = false;
        this.rebuildingDriverId = null;
      }
    },
  },

  created() {
    if (this.driverId) {
      this.filters.driver_id = this.driverId;
    }
    if (this.driver) {
      this.filters.selected_driver = this.driver;
      this.filters.driver_id = this.driver.id;
    }
    if (this.$route?.params?.driver_id || this.$route?.params?.id) {
      const paramId = this.$route.params.driver_id || this.$route.params.id;
      if (paramId && !isNaN(Number(paramId))) {
        this.filters.driver_id = Number(paramId);
      }
    }

    let query = { ...this.$route.query };
    let hasDateChanges = false;

    if (this.$route?.query?.from || this.$route?.query?.start_date) {
      this.filters.from =
        this.$route.query.from || this.$route.query.start_date;
    } else if (this.filters.from) {
      query.from = this.filters.from;
      hasDateChanges = true;
    }

    if (this.$route?.query?.to || this.$route?.query?.end_date) {
      this.filters.to =
        this.$route.query.to || this.$route.query.end_date;
    } else if (this.filters.to) {
      query.to = this.filters.to;
      hasDateChanges = true;
    }

    if (this.$route?.query?.page) {
      this.page = +this.$route.query.page;
      this.paginations.current_page = +this.$route.query.page;
    }

    if (hasDateChanges) {
      this.$router.replace({ query }).catch(() => {});
    }

    this.fetchDrivers();
    this.fetchReportData();
  },
};
</script>

<style scoped>
.driver_operations_report_wrapper {
  animation: fadeIn 0.3s ease-in-out;
}

.header_icon_box {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #783d73 0%, #a05a99 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(120, 61, 115, 0.25);
}

/* Stat Cards */
.ops_stat_card {
  border-radius: 14px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  transition: all 0.25s ease;
  border: 1px solid rgba(0, 0, 0, 0.05);
  background: #ffffff;
  height: 100%;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.ops_stat_card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
}

.card_icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.card_info {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat_label {
  font-size: 11.5px;
  color: #64748b;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stat_val {
  font-size: 19px;
  font-weight: 800;
  margin: 2px 0 0;
  color: #1e293b;
  line-height: 1.2;
}

.stat_sub {
  font-size: 11px;
  color: #94a3b8;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Color Themes */
.card_purple .card_icon {
  background: rgba(120, 61, 115, 0.12);
  color: #783d73;
}
.card_red .card_icon {
  background: rgba(239, 68, 68, 0.12);
  color: #ef4444;
}
.card_green .card_icon {
  background: rgba(16, 185, 129, 0.12);
  color: #10b981;
}
.card_cyan .card_icon {
  background: rgba(6, 182, 212, 0.12);
  color: #0891b2;
}
.card_orange .card_icon {
  background: rgba(249, 115, 22, 0.12);
  color: #f97316;
}
.card_amber .card_icon {
  background: rgba(245, 158, 11, 0.12);
  color: #d97706;
}

/* Filter Box */
.ops_filters_box {
  background: rgba(120, 61, 115, 0.03);
  border: 1px solid rgba(120, 61, 115, 0.1);
}

/* Metric Chips */
.metric_chip {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}

.chip_blue {
  background: #e0f2fe;
  color: #0284c7;
}
.chip_purple {
  background: #f3e8ff;
  color: #7e22ce;
}
.chip_green {
  background: #dcfce7;
  color: #15803d;
}
.chip_amber {
  background: #fef3c7;
  color: #b45309;
}
.chip_red {
  background: #fee2e2;
  color: #b91c1c;
}
.chip_orange {
  background: #ffedd5;
  color: #c2410c;
}
.chip_gray {
  background: #f1f5f9;
  color: #94a3b8;
}
.chip_highlight {
  background: #783d73;
  color: #ffffff;
}
.chip_teal {
  background: #ccfbf1;
  color: #0f766e;
}

.index_badge {
  font-size: 12px;
  font-weight: 700;
  color: #64748b;
}

.driver_link:hover {
  text-decoration: underline;
}

.action_btn {
  white-space: nowrap !important;
  font-size: 11.5px !important;
  font-weight: 600 !important;
  padding: 4px 10px !important;
  border-radius: 6px !important;
  flex-shrink: 0 !important;
  display: inline-flex !important;
  align-items: center !important;
  gap: 5px !important;
  min-height: 28px !important;
}

.action_btn_details {
  border-color: #783d73 !important;
  color: #783d73 !important;
}

.action_btn_details:hover {
  background-color: #783d73 !important;
  color: #ffffff !important;
}

::v-deep .custom_report_table th,
::v-deep .custom_report_table td {
  white-space: nowrap !important;
}

/* =========================================================
   CREATIVE MODAL & DIALOG STYLES
   ========================================================= */

/* Hero Header */
.modal_hero_header {
  background: linear-gradient(
    135deg,
    #42163b 0%,
    #682c63 45%,
    #8e4287 85%,
    #aa58a3 100%
  );
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  position: relative;
  overflow: hidden;
}

.modal_hero_header::before {
  content: "";
  position: absolute;
  top: -40px;
  right: -40px;
  width: 140px;
  height: 140px;
  background: radial-gradient(
    circle,
    rgba(255, 255, 255, 0.15) 0%,
    rgba(255, 255, 255, 0) 70%
  );
  border-radius: 50%;
  pointer-events: none;
}

.hero_avatar_wrapper {
  position: relative;
  width: 52px;
  height: 52px;
  border-radius: 50%;
  border: 2.5px solid rgba(255, 255, 255, 0.9);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  flex-shrink: 0;
  background: #ffffff;
}

.hero_avatar_img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
}

.avatar_badge_icon {
  position: absolute;
  bottom: -2px;
  left: -2px;
  width: 20px;
  height: 20px;
  background: #10b981;
  color: #ffffff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  border: 2px solid #ffffff;
}

.hero_title {
  font-size: 1.15rem;
  letter-spacing: -0.2px;
}

.hero_badge_id {
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  border-radius: 6px;
  padding: 3px 8px;
}

.hero_sub_info {
  font-size: 12.5px;
}

.hero_sub_item {
  color: rgba(255, 255, 255, 0.9);
  display: inline-flex;
  align-items: center;
}

.btn-hero-action {
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: #ffffff;
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.btn-hero-action:hover {
  background: rgba(255, 255, 255, 0.3);
  color: #ffffff;
  transform: translateY(-1px);
}

.btn-icon-close {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  transition: all 0.2s ease;
}

.btn-icon-close:hover {
  background: rgba(255, 255, 255, 0.35);
  transform: rotate(90deg);
}

/* Modal Loader Box */
.modal_loader_box {
  padding: 40px 20px;
}

/* KPI Dashboard Cards */
.modal_kpi_dashboard {
  animation: fadeIn 0.3s ease-in-out;
}

.kpi_stat_card {
  background: #ffffff;
  border-radius: 14px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 14px;
  border: 1px solid #eef2f6;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
  height: 100%;
  transition: all 0.2s ease;
}

.kpi_stat_card:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.06);
}

.kpi_icon_wrapper {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  flex-shrink: 0;
}

.kpi_card_purple .kpi_icon_wrapper {
  background: #f3e8ff;
  color: #783d73;
}

.kpi_card_blue .kpi_icon_wrapper {
  background: #e0f2fe;
  color: #0284c7;
}

.kpi_card_green .kpi_icon_wrapper {
  background: #dcfce7;
  color: #16a34a;
}

.kpi_card_amber .kpi_icon_wrapper {
  background: #fef3c7;
  color: #d97706;
}

.kpi_content {
  flex-grow: 1;
  min-width: 0;
}

.kpi_label {
  font-size: 11.5px;
  font-weight: 600;
  color: #64748b;
  display: block;
}

.kpi_value {
  font-size: 1.25rem;
  font-weight: 800;
  margin: 2px 0 0;
  line-height: 1.2;
}

.text-purple {
  color: #783d73 !important;
}

.kpi_sub {
  font-size: 11px;
  color: #94a3b8;
}

.kpi_dual_row {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 2px;
  font-size: 11.5px;
}

.dual_item {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dual_item small {
  color: #94a3b8;
  margin-left: 4px;
}

.kpi_rate_badge {
  font-size: 12px;
  font-weight: 800;
  padding: 3px 8px;
  border-radius: 6px;
}

.kpi_orders_strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 11px;
  color: #475569;
}

.kpi_orders_strip strong {
  color: #0f172a;
}

/* Alert Mini Pills */
.alert_mini_pill {
  padding: 8px 12px;
  border-radius: 10px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
}

.pill_red {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

.pill_orange {
  background: #ffedd5;
  color: #c2410c;
  border: 1px solid #fdba74;
}

.pill_amber {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}

/* Creative Segmented Tabs */
.creative_tabs_wrapper {
  background: #ede9fe;
  padding: 5px;
  border-radius: 14px;
}

::v-deep .creative_modal_tabs .v-tabs-bar {
  background: transparent !important;
  height: auto !important;
}

::v-deep .creative_modal_tabs .v-tab {
  background: transparent !important;
  border-radius: 10px !important;
  font-weight: 700 !important;
  font-size: 12.5px !important;
  color: #64748b !important;
  padding: 8px 14px !important;
  margin: 0 2px !important;
  min-height: 44px !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
  transition: all 0.25s ease !important;
}

::v-deep .creative_modal_tabs .v-tab--active {
  background: #ffffff !important;
  color: #783d73 !important;
  box-shadow: 0 4px 12px rgba(120, 61, 115, 0.15) !important;
}

.tab_inner_content {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.tab_icon {
  font-size: 15px;
}

.icon_blue {
  color: #0284c7;
}
.icon_green {
  color: #16a34a;
}
.icon_orange {
  color: #ea580c;
}
.icon_red {
  color: #dc2626;
}
.icon_purple {
  color: #9333ea;
}
.icon_indigo {
  color: #4f46e5;
}
.icon_teal {
  color: #0d9488;
}

.tab_counter_chip {
  padding: 2px 7px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 800;
}

/* Creative Table Card */
.creative_table_card {
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.creative_session_table {
  font-size: 13px;
  border-collapse: separate;
  border-spacing: 0;
}

.creative_session_table thead tr {
  background: #f8fafc;
}

.creative_session_table th {
  font-size: 12px;
  font-weight: 700;
  color: #475569;
  padding: 12px 14px;
  border-bottom: 1px solid #e2e8f0;
  border-top: none;
}

.session_row {
  transition: all 0.2s ease;
}

.session_row:hover {
  background-color: #faf5ff !important;
}

.session_row td {
  padding: 11px 14px;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.row_num_badge {
  display: inline-block;
  width: 26px;
  height: 26px;
  line-height: 26px;
  text-align: center;
  background: #f1f5f9;
  color: #64748b;
  font-weight: 800;
  font-size: 11.5px;
  border-radius: 8px;
}

/* DateTime Box */
.dt_cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
}

.dt_time_badge {
  font-weight: 800;
  color: #1e293b;
  font-size: 12.5px;
  display: inline-flex;
  align-items: center;
}

.dt_date_sub {
  font-size: 10.5px;
  color: #94a3b8;
  font-weight: 500;
}

/* Live Active Chip */
.live_active_chip {
  background: #ecfdf5;
  color: #059669;
  border: 1px solid #a7f3d0;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.live_alert_chip {
  background: #fef2f2;
  color: #dc2626;
  border: 1px solid #fecaca;
  padding: 4px 10px;
  border-radius: 20px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.pulse_dot {
  width: 8px;
  height: 8px;
  background: #10b981;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  animation: pulseGreen 1.6s infinite;
}

.pulse_dot_red {
  width: 8px;
  height: 8px;
  background: #ef4444;
  border-radius: 50%;
  box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  animation: pulseRed 1.6s infinite;
}

@keyframes pulseGreen {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(16, 185, 129, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(16, 185, 129, 0);
  }
}

@keyframes pulseRed {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.7);
  }
  70% {
    transform: scale(1);
    box-shadow: 0 0 0 6px rgba(239, 68, 68, 0);
  }
  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(239, 68, 68, 0);
  }
}

/* Duration Pill */
.duration_pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  font-weight: 700;
  font-size: 12px;
}

.duration_active {
  background: #eff6ff;
  color: #1d4ed8;
  border: 1px solid #bfdbfe;
}

.duration_closed {
  background: #f8fafc;
  color: #475569;
  border: 1px solid #e2e8f0;
}

.duration_danger {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
}

/* Source Tag */
.source_tag {
  background: #f8fafc;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
}

/* Status Pill */
.status_pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11.5px;
  font-weight: 700;
}

.pill_open {
  background: #dcfce7;
  color: #15803d;
}

.pill_closed {
  background: #f1f5f9;
  color: #64748b;
}

.pill_success {
  background: #dcfce7;
  color: #15803d;
}

.pill_danger {
  background: #fee2e2;
  color: #b91c1c;
}

.status_dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.dot_gray {
  background: #94a3b8;
}

.dot_pulse {
  background: #16a34a;
}

/* Zone & District Pills */
.zone_status_pill {
  display: inline-flex;
  align-items: center;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
}

.zone_inside {
  background: #dcfce7;
  color: #15803d;
  border: 1px solid #bbf7d0;
}

.zone_outside {
  background: #fef3c7;
  color: #b45309;
  border: 1px solid #fde68a;
}

.district_chip {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #334155;
}

.reason_chip {
  background: #fef2f2;
  color: #991b1b;
  border: 1px solid #fee2e2;
  padding: 3px 9px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 600;
}

.delay_pill {
  background: #fee2e2;
  color: #b91c1c;
  border: 1px solid #fca5a5;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 800;
  display: inline-flex;
  align-items: center;
}

/* Working Times Cards */
.work_day_card {
  transition: all 0.2s ease;
  background: #ffffff;
}

.card_active_day {
  border-color: #c084fc !important;
  background: #faf5ff !important;
}

.card_holiday_day {
  border-color: #e2e8f0 !important;
  opacity: 0.75;
}

.work_time_badge {
  background: #783d73;
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
}

/* Empty State */
.empty_tab_state {
  text-align: center;
}

.empty_state_icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  margin: 0 auto;
}

.bg_light_blue {
  background: #e0f2fe;
}
.bg_light_green {
  background: #dcfce7;
}
.bg_light_purple {
  background: #f3e8ff;
}
.bg_light_teal {
  background: #ccfbf1;
}

.text-teal {
  color: #0d9488 !important;
}

.order_ref_badge {
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
  color: #0f766e;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11.5px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
  text-decoration: none !important;
}

.order_ref_link:hover {
  background: #0d9488;
  color: #ffffff !important;
  border-color: #0d9488;
  transform: translateY(-1px);
}

.order_ref_link:hover i {
  color: #ffffff !important;
}

/* Modal Footer & Buttons */
.modal_custom_footer {
  border-top: 1px solid #e2e8f0;
}

.btn-gradient-primary {
  background: linear-gradient(135deg, #783d73 0%, #a05a99 100%);
  color: #ffffff;
  border: none;
  box-shadow: 0 4px 12px rgba(120, 61, 115, 0.25);
  transition: all 0.2s ease;
}

.btn-gradient-primary:hover {
  background: linear-gradient(135deg, #672f62 0%, #8e4787 100%);
  color: #ffffff;
  box-shadow: 0 6px 16px rgba(120, 61, 115, 0.35);
  transform: translateY(-1px);
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Orders & Nominations Summary Banner */
.ops_nominations_summary_banner {
  background: linear-gradient(
    135deg,
    rgba(120, 61, 115, 0.04) 0%,
    rgba(255, 255, 255, 0.95) 100%
  );
  border: 1px solid rgba(120, 61, 115, 0.12);
}

.summary_mini_pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid transparent;
}

.summary_mini_pill strong {
  font-weight: 800;
  font-size: 13px;
}

.summary_mini_pill.pill_purple {
  background: #f3e8ff;
  color: #6b21a8;
  border-color: rgba(107, 33, 168, 0.15);
}

.summary_mini_pill.pill_red {
  background: #fee2e2;
  color: #991b1b;
  border-color: rgba(153, 27, 27, 0.15);
}

.summary_mini_pill.pill_amber {
  background: #fef3c7;
  color: #92400e;
  border-color: rgba(146, 64, 14, 0.15);
}

.summary_mini_pill.pill_orange {
  background: #ffedd5;
  color: #9a3412;
  border-color: rgba(154, 52, 18, 0.15);
}

.summary_mini_pill.pill_blue {
  background: #e0f2fe;
  color: #075985;
  border-color: rgba(7, 89, 133, 0.15);
}

.summary_mini_pill.pill_pink {
  background: #ffe4ec;
  color: #9d174d;
  border-color: rgba(157, 23, 77, 0.15);
}

/* Modal Nominations Breakdown Box */
.nominations_breakdown_box {
  background: #ffffff;
  border: 1px solid rgba(120, 61, 115, 0.12);
}

.breakdown_icon_badge {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: linear-gradient(135deg, #783d73 0%, #a05a99 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
}

.nom_stat_card {
  padding: 10px 8px;
  border-radius: 10px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  transition: all 0.2s ease;
  height: 100%;
}

.nom_stat_card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.card_accepted {
  background: #f0fdf4;
  border-color: rgba(22, 163, 74, 0.2);
}
.card_assigned {
  background: #faf5ff;
  border-color: rgba(126, 34, 206, 0.2);
}
.card_expired {
  background: #fef2f2;
  border-color: rgba(220, 38, 38, 0.2);
}
.card_taken {
  background: #fffbeb;
  border-color: rgba(217, 119, 6, 0.2);
}
.card_closed {
  background: #fff7ed;
  border-color: rgba(234, 88, 12, 0.2);
}
.card_pending {
  background: #f0f9ff;
  border-color: rgba(2, 132, 199, 0.2);
}
.card_not_accepted {
  background: #f8fafc;
  border-color: rgba(148, 163, 184, 0.2);
}

.nom_stat_title {
  font-size: 11px;
  font-weight: 700;
  color: #475569;
  white-space: nowrap;
}

.nom_stat_num {
  font-size: 18px;
  font-weight: 800;
  margin: 2px 0 0;
  line-height: 1.2;
}

.nom_stat_sub {
  font-size: 10px;
  color: #94a3b8;
  white-space: nowrap;
}

.text-purple {
  color: #7e22ce !important;
}
.text-amber {
  color: #b45309 !important;
}
.text-cyan {
  color: #0284c7 !important;
}

/* In-cell Nomination Chips */
.nominations_cell_chips {
  gap: 3px !important;
}

.nom_chip {
  display: inline-flex;
  align-items: center;
  padding: 1.5px 6px;
  border-radius: 4px;
  font-size: 10.5px;
  font-weight: 500;
  line-height: 1.3;
  border: 1px solid transparent;
  white-space: nowrap;
}

.nom_chip strong {
  font-weight: 700;
  margin-right: 2px;
}

.nom_chip_muted {
  background: #f1f5f9;
  color: #64748b;
  border-color: #e2e8f0;
}

.nom_chip_purple {
  background: #f3e8ff;
  color: #7e22ce;
  border-color: rgba(126, 34, 206, 0.2);
}

.nom_chip_red {
  background: #fee2e2;
  color: #b91c1c;
  border-color: rgba(185, 28, 28, 0.2);
}

.nom_chip_amber {
  background: #fef3c7;
  color: #b45309;
  border-color: rgba(180, 83, 9, 0.2);
}

.nom_chip_orange {
  background: #ffedd5;
  color: #c2410c;
  border-color: rgba(194, 65, 12, 0.2);
}

.nom_chip_blue {
  background: #e0f2fe;
  color: #0284c7;
  border-color: rgba(2, 132, 199, 0.2);
}

/* Creative Pagination Toolbar */
.creative_pagination_toolbar {
  background: linear-gradient(180deg, #ffffff 0%, #fcfaff 100%);
  border: 1px solid #ede8f2;
  border-radius: 16px;
  padding: 14px 22px;
  box-shadow: 0 4px 20px -2px rgba(120, 61, 115, 0.06), 0 2px 6px -1px rgba(0, 0, 0, 0.03);
  position: relative;
  overflow: hidden;
}

.creative_pagination_toolbar::before {
  content: "";
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  height: 3px;
  background: linear-gradient(90deg, #783d73 0%, #a05a99 50%, #e2d1df 100%);
  border-radius: 16px 16px 0 0;
}

/* Part 1: Tracker Group */
.tracker_icon_box {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: rgba(120, 61, 115, 0.08);
  color: #783d73;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.creative_pagination_toolbar:hover .tracker_icon_box {
  transform: scale(1.05);
}

.tracker_info {
  display: flex;
  flex-direction: column;
}

.tracker_label {
  font-size: 12px;
  font-weight: 600;
  color: #64748b;
}

.total_badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #783d73 0%, #904489 100%);
  color: #ffffff;
  font-size: 11.5px;
  font-weight: 700;
  padding: 1px 9px;
  border-radius: 20px;
  line-height: 1.4;
  box-shadow: 0 2px 6px rgba(120, 61, 115, 0.25);
}

.tracker_sub {
  font-size: 12.5px;
  color: #475569;
  margin-top: 2px;
}

/* Part 2: Segmented Pills */
.per_page_title {
  font-size: 12.5px;
  font-weight: 600;
  color: #64748b;
}

.segmented_pills_container {
  display: inline-flex;
  align-items: center;
  background: #f1f3f7;
  padding: 4px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  gap: 3px;
}

.seg_pill_btn {
  border: none;
  outline: none;
  background: transparent;
  color: #64748b;
  font-size: 12.5px;
  font-weight: 600;
  padding: 5px 12px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1.2;
}

.seg_pill_btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.9);
  color: #783d73;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.seg_pill_btn.active {
  background: linear-gradient(135deg, #783d73 0%, #904489 100%);
  color: #ffffff;
  box-shadow: 0 2px 8px rgba(120, 61, 115, 0.35);
  transform: scale(1.02);
}

/* Part 3: Navigation Section */
.single_page_badge {
  display: inline-flex;
  align-items: center;
  background: #fdf2f8;
  color: #783d73;
  border: 1px solid rgba(120, 61, 115, 0.15);
  border-radius: 20px;
  padding: 6px 14px;
  font-size: 12.5px;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(120, 61, 115, 0.05);
}

/* Vuetify Pagination Custom Polish */
.custom_v_pagination >>> .v-pagination__item {
  border-radius: 8px !important;
  font-size: 13px !important;
  font-weight: 600 !important;
  min-width: 34px !important;
  height: 34px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  transition: all 0.2s ease !important;
}

.custom_v_pagination >>> .v-pagination__item--active {
  background: linear-gradient(135deg, #783d73 0%, #904489 100%) !important;
  color: #ffffff !important;
  border-color: #783d73 !important;
  box-shadow: 0 3px 10px rgba(120, 61, 115, 0.3) !important;
}

.custom_v_pagination >>> .v-pagination__navigation {
  border-radius: 8px !important;
  min-width: 34px !important;
  height: 34px !important;
  box-shadow: none !important;
  border: 1px solid #e2e8f0 !important;
  background: #ffffff !important;
  transition: all 0.2s ease !important;
}

.custom_v_pagination >>> .v-pagination__navigation:hover:not(.v-pagination__navigation--disabled) {
  border-color: #783d73 !important;
  color: #783d73 !important;
  background: #fcf8fc !important;
}
</style>
